import { useState, useEffect, useRef } from "react";
import {
    X,
    Building2,
    BriefcaseIcon,
    Share2,
    Maximize2,
    Minimize2,
    MessageSquareIcon,
    CheckIcon,
    AlertCircleIcon,
    HeartIcon,
    CheckCircleIcon,
    Bookmark,
    ChevronDownIcon,
    Download,
    FileIcon,
    FileXIcon,
    ClipboardListIcon,
    Loader2Icon,
    SendIcon,
    UserIcon,
    StarIcon,
    HistoryIcon,
    WorkflowIcon,
    FlagIcon,
    ZapIcon,
    RotateCcwIcon,
} from "lucide-react";
import { STEP_TYPE_LABELS as FLOW_STEP_TYPE_LABELS } from "@type/hiring-flow";
import {
    advanceApplicationFlow,
    isClientTimeout,
    FLOW_TIMEOUT_TITLE,
    FLOW_TIMEOUT_HINT,
    resolveFlowGate,
    FlowGateResolution,
    TriggerStepResult,
} from "@service/hiring-flow";
import TriggerStepDialog from "@container/dialog/dialog-trigger-step";
import RejectCandidateWhatsappDialog from "@component/job-reject-whatsapp-dialog";
import FlowGateResolveConfirmDialog from "@container/dialog/dialog-flow-gate-resolve-confirm";
import WhatsappScreeningInvitationDialog from "@container/dialog/dialog-whatsapp-screening-invitation";
import { Button } from "@component/button";
import { Textarea } from "@component/textarea";
import { cn } from "@util/tailwind";
import {
    CandidateActivityGroup,
    CandidateInterviewStatus,
    CvData,
    SearchShortlistedCandidateRequest,
    SendCustomizedEmailRequest,
    ShortlistedCandidateItem,
    ShortlistedCounts,
    ShortlistedSearchType,
    User as UserType,
} from "@type/candidate";
import { UserInterview, UserSubmissionJob } from "@type/assessments";
import {
    applicantUpdateStatus,
    getCandidateActivityHistories,
    inviteToApply,
    getListJobApplicant,
    deleteShortlistCandidate,
    createApplicationComment,
    getApplicantEligibilityCheck,
} from "@service/job";
import type { ApplicantEligibilityCheck } from "@service/job";
import { InterviewPlayer } from "@component/interview-player";
import { getCandidateInterviewStatus, getInterviewInsights, getVideo } from "@service/interview";
import { getUserInterviewSubmission } from "@service/assessment";
import { InterviewInsight, InterviewVideo } from "@type/videoInterview";
import {
    applicationStatusMap,
    defaultPagination,
    interviewStatusNone,
    interviewStatusPendingInterview,
} from "@constant/app";
import {
    convertCapitalizeWords,
    convertToLowerSnakeCase,
    getApplicationStatusClass,
    getInvitationBannerClass,
    getInvitationBorderClass,
} from "@util/string";
import { parseFormat } from "@util/date";
import { getMatchBadgeClasses } from "@util/job";
import { InviteToApplyRequest, Job, ReviewScheduledNoteItem, JobApplicantItem } from "@type/job";
import { Applicant } from "@type/applicant";
import { createContractV2 } from "@service/contract";
import { CreateContractRequest } from "@type/contract";
import { sendCustomizedEmailV2 } from "@service/employer";
import { getSubdomainHost } from "@util/name";
import { useUserProfileContext } from "@context/user_profile";
import { useSubscription } from "@context/subscription_context";
import { useMaintenanceGate } from "@context/maintenance";
import { useSnackbar } from "@hook/use-snackbar";
import { getCookie, setCookie } from "@util/cookie";
import {
    bookmarkingCandidate,
    removeBookmarkCandidate,
    searchShortlistedCandidate,
    getShortlistedByCandidateAndJobID,
    getShortlistedByCandidateID,
    getShortlistedCounts,
} from "@service/user";
import { catchErrorLog } from "@util/sentry";
import InviteConfirmationDialogV2 from "@component/invite-confirmation-v2-dialog";
import RejectCandidateDialogV2 from "@component/job-reject-v2-dialog";
import InterviewInvitationDialogV2 from "@component/interview-invitation-v2-dialog";
import JobOfferDialogV2 from "@component/job-offer-v2-dialog";
import ShortlistToInviteDialog from "@container/dialog/dialog-shortlist-invite";
import { MetaPagination } from "@type/common";
import DropdownApplicantStatus from "@component/dropdown-applicant-status";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from "@component/dropdown-menu";
import AssessmentInvitationDialogV2 from "@component/assessment-invitation-v2-dialog";
import ReviewScheduleManagerDialog from "@component/review-schedule-manager-dialog";
import InternalOfferNoteDialog from "@container/dialog/dialog-internal-offer-note";
import { getApplicantDisplayCV, getUserSubmissionJob } from "@service/cv";
import { ApplicantCVDisplay } from "@type/cv";
import { PDFViewer } from "@component/pdf-viewer";
import JobApplicationsList from "@container/job-applications-list";
import ProfileTabContent from "./tabs/ProfileTabContent";
import AiInterviewTabContent from "./tabs/AiInterviewTabContent";
import FlowDocumentsTabContent from "./tabs/FlowDocumentsTabContent";
import ActivityLogTabContent from "./tabs/ActivityLogTabContent";
import FormTabContent from "./tabs/FormTabContent";
import ReviewTabContent from "./tabs/ReviewTabContent";
import MessagesTabContent, { type MessagesTabError } from "./tabs/MessagesTabContent";
import WhatsappReplyComposer from "./tabs/whatsapp-reply-composer";
import { getCandidateWhatsappThread, WhatsappThreadForbiddenError } from "@service/whatsapp";
import type { WhatsappThreadResult } from "@type/whatsapp";
import SWEExperienceSurvey from "@container/eligibility-check-job";

interface ProfileDrawerProps {
    cvID: string;
    candidate: CvData;
    user?: UserType;
    isOpen: boolean;
    selectedActiveTab?: string | undefined;
    onClose: () => void;

    // applicant
    activeFunnelStage?: string;
    job?: Job | null;
    applicant?: Applicant;
    isShowPercentMatch?: boolean;
    applicant_status?: string;
    similarity_score?: number | null;
    isShowHireButton?: boolean;
    isSuggestionCandidate?: boolean;
    isSavedBookmark?: boolean;
    onReload?: () => void;
    reloadSavedBookmark?: number;
    onReloadSavedBookmark?: () => void;
    /** When true, auto-focuses the comment composer when the drawer opens on the Review tab. */
    autoFocusComposer?: boolean;
}

export default function DrawerCandidateProfile({
    cvID,
    candidate,
    selectedActiveTab = undefined,
    user,
    isOpen,
    job,
    applicant,
    onClose,
    onReload,
    isShowPercentMatch = false,
    applicant_status = "",
    activeFunnelStage = "",
    similarity_score = 0,
    isShowHireButton = false,
    isSuggestionCandidate = false,
    reloadSavedBookmark = 0,
    onReloadSavedBookmark,
    autoFocusComposer = false,
}: ProfileDrawerProps) {
    const hasNoEmail = !applicant?.main_email || applicant.main_email.trim() === "";
    // #3478: the screening invitation can also go out on WhatsApp, which is the
    // only route for a CV-uploaded candidate with a phone number but no email.
    // Presence check only — the BE preview (#4533) rules on validity.
    const hasNoPhone = !applicant?.phone_number || applicant.phone_number.trim() === "";
    // The job runs a WhatsApp-first hiring flow — reminders don't apply (candidates
    // are driven over WhatsApp) and rejections can be sent over WhatsApp instead.
    const isWaHiringFlow =
        (job as { hiring_flow?: { channel?: string } } | null | undefined)?.hiring_flow?.channel === "whatsapp_first"
        || applicant?.channel === "whatsapp_first";
    // #3186: gate-flagged candidate (Knockout Flagged / Deadline Missed). These
    // land in the ATS "Flagged" tab; the drawer footer mirrors the row's actions.
    const flagStatusKey = convertToLowerSnakeCase(applicant?.status || "");
    const isFlaggedCandidate = !!applicant?.paused_at && (flagStatusKey === "knockout_flagged" || flagStatusKey === "deadline_missed");
    const isKnockoutFlag = flagStatusKey === "knockout_flagged";
    const flagSuggestJobTitle = applicant?.paused_at?.suggest_job?.title;
    const snackbar = useSnackbar();
    const [isFlowActionLoading, setFlowActionLoading] = useState<boolean>(false);
    // #3186/#3877: WhatsApp reject dialog for a flagged candidate with no email.
    const [showWaRejectDialog, setShowWaRejectDialog] = useState<boolean>(false);
    // #3242: HR "Trigger Step" picker (Messages tab).
    const [showTriggerStepDialog, setShowTriggerStepDialog] = useState<boolean>(false);
    const [isLoadingUserInfo, currentUserInfo] = useUserProfileContext();
    const { showTrialUsageLimitDialog, isWorkspaceRestricted, isAssessmentActionBlocked , maintenanceTooltip} = useSubscription();
    // CSS-only inert + dim — applied to the comment composer and the footer
    // action button row when the workspace is restricted (trial expired
    // beyond the 7-day grace), so we don't have to thread `disabled`
    // through every dropdown / nested button.
    //
    // #3573: maintenance dims the same two rows. It used to reach only the
    // tooltip below, which meant a window announced that changes were paused
    // and then left every button live — the notice and the behaviour
    // disagreed, and the buttons were the half users believed.
    const maintenance = useMaintenanceGate();
    const trialExpiredInert = isWorkspaceRestricted || maintenance.blocked
        ? `pointer-events-none opacity-50`
        : "";
    const trialExpiredTooltip = maintenanceTooltip
        ?? (isWorkspaceRestricted
            ? "Your trial has expired. Upgrade to take action on candidates."
            : undefined);
    // Assessment-consuming candidate actions (send invitation, resend, reminder,
    // retake) are blocked when the AI-assessment cap is hit OR the workspace is
    // restricted — `isAssessmentActionBlocked` covers both. A CV cap alone does
    // not block these.
    // AI Interview / Form / Eligibility Check are now sub-tabs inside one
    // "Assessment" top-level tab. Map any deep-link to those old tabs onto it.
    const ASSESSMENT_SUBTABS = ["ai-interview", "form", "eligibility-check"];
    const resolveTopTab = (t?: string) =>
        t && ASSESSMENT_SUBTABS.includes(t) ? "assessment" : (t || "profile");
    const [activeTab, setActiveTab] = useState<string>(resolveTopTab(selectedActiveTab));
    const [assessmentSubTab, setAssessmentSubTab] = useState<string>(
        selectedActiveTab && ASSESSMENT_SUBTABS.includes(selectedActiveTab)
            ? selectedActiveTab
            : "ai-interview"
    );
    // CV tab sub-view (#3146): "cv" (parsed CV) or "documents" (collected files).
    // The Documents sub-tab only appears when hiring flow is on (showDocumentsTab).
    const [cvSubTab, setCvSubTab] = useState<string>("cv");
    const [activeSection, setActiveSection] = useState<string>("");
    const [inviteDialogOpen, setInviteDialogOpen] = useState<boolean>(false);
    const [shortlistDialogOpen, setShortlistDialogOpen] = useState<boolean>(false);
    const [countShortlisted, setCountShortlisted] = useState<number>(0);
    const [shortlistedItems, setShortlistedItems] = useState<ShortlistedCandidateItem[]>([]);
    const [isLoadingInvitation, setLoadingInvitation] = useState<boolean>(false);
    const [isLoadingVideo, setLoadingVideo] = useState<boolean>(true);
    const [isSuccessSentInvitation, setSuccessInvitation] = useState<boolean>(false);
    const [isLoadingInterviewInsight, setLoadingInterviewInsight] = useState<boolean>(true);
    const [videoInterview, setVideoInterview] = useState<InterviewVideo | undefined>(undefined);
    const [interviewStatus, setInterviewStatus] = useState<CandidateInterviewStatus | undefined>(undefined)
    const [videoDuration, setVideoDuration] = useState<number | null>(null);
    const [interviewInsights, setInterviewInsights] = useState<InterviewInsight | null>(null);
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
    const [showRejectOfferDialog, setShowRejectOfferDialog] = useState<boolean>(false)
    const [showInterviewInvitationDialog, setShowInterviewInvitationDialog] =
        useState<boolean>(false);
    const [showJobOfferDialog, setShowJobOfferDialog] = useState(false);
    const [isMarkOfferResent, setIsMarkOfferResent] = useState<boolean>(false);
    const [isReviewScheduleManagerDialogOpen, setReviewScheduleManagerDialogOpen] = useState<boolean>(false);
    const [isInternalOfferNoteDialogOpen, setIsInternalOfferNoteDialogOpen] = useState<boolean>(false);
    const [isSaved, setIsSaved] = useState<boolean>(false);
    const [applicantNewStatus, setApplicantNewStatus] = useState<string>(applicant_status || "");
    const [activeTabReview, setActiveTabReview] = useState<"applicant" | "candidate" | string>(
        job ? "applicant" : "candidate"
    );
    // WhatsApp thread (Maya) — page 1 (newest) is fetched on drawer open so the
    // Messages tab can show an unread dot before it's opened. That same fetch
    // marks the page read server-side, so `whatsappHasUnread` (derived from the
    // returned hr_read flags) is a one-shot cue we also clear locally on open.
    const candidateWhatsappId = applicant?.applicant_id || user?.id || "";
    // Display name for the header/threads — candidate-mode openers (Talent
    // Pool, admin Users #3372) may carry no applicant/CV data, so the user
    // prop's name is the last resort before the em-dash placeholder.
    const displayCandidateName =
        applicant?.applicant_name
        || candidate?.CV_data?.name
        || [user?.first_name, user?.last_name].filter(Boolean).join(" ");
    // The Messages tab (and its thread fetch) is gated on the company having
    // Maya (the WhatsApp chatbot) enabled — from GET /auth/me. Platform admins
    // have no company (so no maya_enabled) but must still read candidate
    // threads from the admin Users drawer (#3372) — the BE thread read stays
    // the real access gate for them.
    const mayaEnabled =
        !!currentUserInfo?.maya_enabled
        || ["admin", "super_admin"].includes(currentUserInfo?.role || "");
    const [whatsappThread, setWhatsappThread] = useState<WhatsappThreadResult | null>(null);
    const [whatsappThreadError, setWhatsappThreadError] = useState<MessagesTabError>(null);
    const [whatsappThreadLoading, setWhatsappThreadLoading] = useState<boolean>(false);
    const [whatsappHasUnread, setWhatsappHasUnread] = useState<boolean>(false);
    // Bumped after the footer composer sends a reply, to re-fetch the thread so
    // the new `hr_reply` row shows in the Messages tab.
    const [whatsappReloadKey, setWhatsappReloadKey] = useState<number>(0);
    // Idempotency for the thread fetch: page 1 is fetched at most once per
    // (candidate, reloadKey) while the drawer stays open, so switching tabs never
    // refetches. requestRef ignores a superseded candidate switch's result without
    // cancelling (so `finally` always clears loading — no stuck spinner).
    const whatsappThreadRequestRef = useRef(0);
    const whatsappThreadFetchKeyRef = useRef<string | null>(null);
    const [cvDisplay, setCvDisplay] = useState<ApplicantCVDisplay | null>(null);
    const [formDisplay, setFormDisplay] = useState<UserSubmissionJob | null>(null);
    // Eligibility Check (#feat/applicant-eligibility-check) — null until fetched
    // or when the application has no eligibility data (tab hidden).
    const [eligibilityCheck, setEligibilityCheck] = useState<ApplicantEligibilityCheck | null>(null);
    const [showAssessmentInvitationDialog, setShowAssessmentInvitationDialog] = useState<boolean>(false);
    // #3478: screening invitation over WhatsApp (preview then send).
    const [showWaInvitationDialog, setShowWaInvitationDialog] = useState<boolean>(false);
    // Confirm before waiving a knockout flag — it overrules a screening condition
    // HR configured and restarts the flow the gate paused.
    const [showKeepInJobConfirm, setShowKeepInJobConfirm] = useState<boolean>(false);
    const [showArchiveFlaggedConfirm, setShowArchiveFlaggedConfirm] = useState<boolean>(false);
    const [showAssessmentResendDialog, setShowAssessmentResendDialog] = useState<boolean>(false);
    const [showAssessmentInvitationReminderDialog, setShowAssessmentInvitationReminderDialog] = useState<boolean>(false);
    const [showAssessmentRetakeDialog, setShowAssessmentRetakeDialog] = useState<boolean>(false);

    const contentRef = useRef<HTMLDivElement>(null);
    // The sticky identity + tab-nav header; its height offsets scroll-to-section
    // so a section never lands hidden behind it.
    const stickyHeaderRef = useRef<HTMLDivElement>(null);

    // ---- Customizable Hiring Flow profile surfaces (#3076) -------------------
    // Stepper + Flow tab render only for flow-managed candidates: the board row
    // carries flow metadata (BE #3640) and/or the job has a custom flow.
    // Per-company rollout flag (#3084): require an EXPLICIT true before showing
    // any flow UI — absent field (old BE / partial rollout) ⇒ OFF, matching the
    // stated contract, so the stepper/Flow tab never surface fail-open.
    const hiringFlowEnabled = (job as { company_hiring_flow_enabled?: boolean } | null | undefined)?.company_hiring_flow_enabled === true;
    const hasHiringFlow = hiringFlowEnabled
        && !!(applicant?.flow_progress?.total || applicant?.current_step || (job as { hiring_flow_is_custom?: boolean } | null | undefined)?.hiring_flow_is_custom);
    // Flow surfaces on the candidate profile — the compact stepper above the tabs
    // and the "Flow" tab — are hidden per product request. The flow logic/data is
    // retained (gated) so re-enabling is a one-line flip.
    const showFlowSurface = false;
    const hiringFlowSteps = (job as { hiring_flow?: { steps?: { id: string; type: string; label?: string }[] } } | null | undefined)?.hiring_flow?.steps || [];
    // Documents tab (#3146) — only for hiring-flow candidates whose flow has a
    // Document Collection step (where the chat-native uploads land). `applicant.id`
    // is the application id the flow-documents endpoint takes.
    const hasDocumentsStep = hiringFlowSteps.some((s) => s.type === "documents");
    const showDocumentsTab = hasHiringFlow && hasDocumentsStep && !!applicant?.id;
    // 1-based index of the candidate's current step; null once the flow is done
    // (Ready for Review onward → every step reads as completed).
    const flowCurrentIndex = applicant?.current_step?.index ?? null;
    const flowIsDone = !flowCurrentIndex && ["to_review", "offers", "closed"].includes(activeFunnelStage || "");
    const flowStepLabel = (step: { type: string; label?: string }) =>
        step.label || FLOW_STEP_TYPE_LABELS[step.type as keyof typeof FLOW_STEP_TYPE_LABELS] || step.type;
    // #3297 (BE ourteam-backend#4031): every step completed but the terminal
    // transition never fired — stuck in Screening In Progress. The footer swaps
    // its primary action for "Move to Ready for Review" (ordinary flow/advance).
    const flowStuckCompleted =
        hasHiringFlow &&
        !applicant?.paused_at &&
        !applicant?.current_step &&
        (applicant?.flow_progress?.total ?? 0) > 0 &&
        applicant?.flow_progress?.completed === applicant?.flow_progress?.total;
    // Gate events + pinned version — fetched lazily when the Flow tab opens,
    // from the by-candidate read (additive fields, ourteam-backend PR #3657).
    const [flowGateEvents, setFlowGateEvents] = useState<{ step_id?: string; gate_type?: string; result?: string; at?: string; resolution?: { action?: string; by?: string; at?: string } | string | null; reason?: string }[]>([]);
    const [flowPinnedVersion, setFlowPinnedVersion] = useState<number | null>(applicant?.flow_version ?? null);
    // Guard + reset so a reused drawer (candidate A → B) can't leak A's gate
    // history/version into B's Flow tab if A's request resolves late.
    const flowTabRequestRef = useRef(0);
    useEffect(() => {
        if (activeTab !== "flow" || !user?.id || !job?.id) return;
        const requestId = ++flowTabRequestRef.current;
        setFlowGateEvents([]);
        setFlowPinnedVersion(applicant?.flow_version ?? null);
        getListJobApplicant(user.id).then((resp) => {
            if (requestId !== flowTabRequestRef.current) return;
            const rows = resp?.data?.data || [];
            const row = rows.find((r: { job_id?: string }) => r.job_id === job.id);
            setFlowGateEvents(row?.gate_events || []);
            setFlowPinnedVersion(row?.hiring_flow_version ?? applicant?.flow_version ?? null);
        }).catch((error: unknown) => {
            if (requestId !== flowTabRequestRef.current) return;
            catchErrorLog(`Failed to load flow gate events: ${error}`);
        });
    }, [activeTab, user?.id, job?.id, applicant?.flow_version]);
    // ---------------------------------------------------------------------------
    const composerTextareaRef = useRef<HTMLTextAreaElement>(null);
    const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({
        personal: null,
        skills: null,
        experience: null,
        education: null,
        awards: null,
        certifications: null,
        projects: null,
    });
    const [isLoadingShortlistedCandidate, setLoadingShortlistedCandidate] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [shortlistedCandidates, setShortlistedCandidates] = useState<ShortlistedCandidateItem[]>([]);
    // Drives the /search/shortlisted `type` query param. Defaults to "all" so
    // the initial fetch returns both favourites and comments.
    const [reviewType, setReviewType] = useState<ShortlistedSearchType>("all");
    // Server-reported counts for the filter chips. Driven by /shortlisted/counts
    // and refreshed alongside the list whenever the review tab data changes.
    const [reviewCounts, setReviewCounts] = useState<ShortlistedCounts>({
        all: 0,
        favorites: 0,
        comments: 0,
    });
    const [metaPagination, setMetaPagination] = useState<MetaPagination>({
        limit: 0,
        page: 1,
        total_pages: 0,
        total: 0,
    })
    const [candidateActivityGroups, setCandidateActivityGroups] = useState<CandidateActivityGroup[]>([]);
    const [jobApplicantItems, setJobApplicantItems] = useState<JobApplicantItem[]>([])
    const [, setCommentRefreshKey] = useState(0);
    const [drawerComposerText, setDrawerComposerText] = useState("");
    const [isDrawerComposerPosting, setIsDrawerComposerPosting] = useState(false);

    useEffect(() => {
        if (job && user) {
            getUserSubmissionJob(job?.id || "", user.id).then((resp) => {
                if (resp.status === 200) {
                    setFormDisplay(resp.data.data);
                }
            })
        }
    }, [job, user])

    // Eligibility Check tab data — HR view of the candidate's answers for THIS
    // application. The service returns null on 403/404 (no permission / no
    // application), and the tab only shows when the answers are non-null.
    useEffect(() => {
        setEligibilityCheck(null);
        const applicantId = applicant?.applicant_id;
        if (!job?.id || !applicantId) return;
        let cancelled = false;
        getApplicantEligibilityCheck(job.id, applicantId).then((data) => {
            if (!cancelled) setEligibilityCheck(data);
        });
        return () => { cancelled = true; };
    }, [job?.id, applicant?.applicant_id])

    useEffect(() => {
        if (applicant?.applicant_id && job?.id) {
            getShortlistedByCandidateAndJobID(applicant.applicant_id, job.id).then((res) => {
                if (res.status === 200) {
                    const items = res.data.data || [];
                    setShortlistedItems(items);
                    setCountShortlisted(items.length);
                }
            });
        } else if (user) {
            getShortlistedByCandidateID(user.id).then((res) => {
                if (res.status === 200) {
                    const items = res.data.data || [];
                    setShortlistedItems(items);
                    setCountShortlisted(items.length);
                }
            });
        }
    }, [applicant?.applicant_id, job?.id, user])

    // Pulls the chip-badge counts (all / favorites / comments). Independent of
    // the current `type` filter so the badges stay correct on every tab.
    const refreshReviewCounts = () => {
        const candidate_id = applicant ? applicant.applicant_id : user?.id || "";
        if (!candidate_id) return;
        getShortlistedCounts({
            candidate_id,
            view: job ? "applicant" : "candidate",
            job_id: job ? job.id : undefined,
        })
            .then((resp) => {
                if (resp.status === 200 && resp.data?.data) {
                    setReviewCounts(resp.data.data);
                }
            })
            .catch(() => {
                // Silent — chip badges just stay at their last known values.
            });
    };

    useEffect(() => {
        if (activeTab !== "review") return;
        setShortlistedCandidates([]);
        setLoadingShortlistedCandidate(true);
        const params: SearchShortlistedCandidateRequest = {
            page: currentPage,
            limit: defaultPagination.limit,
            candidate_id: applicant ? applicant.applicant_id : user?.id || "",
            view: job ? "applicant" : "candidate",
            job_id: job ? job.id : undefined,
            type: reviewType,
        }

        searchShortlistedCandidate(params).then((resp) => {
            if (resp.status === 200) {
                setShortlistedCandidates(resp.data.data);
                setMetaPagination(resp.data.meta.pagination);
            }
        }).finally(() => {
            setLoadingShortlistedCandidate(false);
        });

        refreshReviewCounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, applicant, user, activeTab, reviewType])

    useEffect(() => {
        setActiveTab(resolveTopTab(selectedActiveTab));
        if (selectedActiveTab && ASSESSMENT_SUBTABS.includes(selectedActiveTab)) {
            setAssessmentSubTab(selectedActiveTab);
        }
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
        setVideoInterview(undefined);
        setSuccessInvitation(false);
    }, [candidate, selectedActiveTab])

    useEffect(() => {
        if (!isOpen || !autoFocusComposer || activeTab !== "review") return;
        const timer = setTimeout(() => {
            composerTextareaRef.current?.focus();
        }, 200);
        return () => clearTimeout(timer);
    }, [isOpen, autoFocusComposer, activeTab])

    // WhatsApp thread: fetch page 1 (newest) once when the drawer opens for a
    // candidate. Powers both the Messages tab and the unread dot. This fetch is
    // what marks the page read on the BE — no separate mark-read call.
    useEffect(() => {
        if (!isOpen || !candidateWhatsappId || !mayaEnabled) {
            setWhatsappThread(null);
            setWhatsappThreadError(null);
            setWhatsappHasUnread(false);
            // Reset so reopening the drawer refetches (and re-marks the page read).
            whatsappThreadFetchKeyRef.current = null;
            return;
        }
        // Fetch page 1 at most ONCE per (candidate, reloadKey) for the lifetime of
        // this open drawer. Switching tabs must never refetch the thread — only a
        // candidate change, a sent reply (reloadKey), or reopening the drawer do.
        const fetchKey = `${candidateWhatsappId}::${whatsappReloadKey}`;
        if (whatsappThreadFetchKeyRef.current === fetchKey) return;
        whatsappThreadFetchKeyRef.current = fetchKey;

        const requestId = ++whatsappThreadRequestRef.current;
        setWhatsappThreadLoading(true);
        setWhatsappThreadError(null);
        getCandidateWhatsappThread(candidateWhatsappId, 1)
            .then((result) => {
                if (requestId !== whatsappThreadRequestRef.current) return;
                setWhatsappThread(result);
                setWhatsappHasUnread(
                    result.messages.some(
                        (m) => m.direction === "inbound" && !m.hr_read
                    )
                );
            })
            .catch((err) => {
                if (requestId !== whatsappThreadRequestRef.current) return;
                // A failed fetch must be retryable — don't leave the key marked as
                // fetched, or reopening/reloading couldn't try again.
                whatsappThreadFetchKeyRef.current = null;
                setWhatsappThread(null);
                setWhatsappHasUnread(false);
                if (err instanceof WhatsappThreadForbiddenError) {
                    setWhatsappThreadError("forbidden");
                } else {
                    setWhatsappThreadError("error");
                    catchErrorLog(`[whatsapp-thread] load failed: ${err}`);
                }
            })
            .finally(() => {
                if (requestId === whatsappThreadRequestRef.current) setWhatsappThreadLoading(false);
            });
    }, [isOpen, candidateWhatsappId, whatsappReloadKey, mayaEnabled])

    // created_at of the candidate's most recent inbound message — drives the
    // composer's 24h free-form-window hint. Messages come oldest → newest.
    const whatsappLastInboundAt = (() => {
        const msgs = whatsappThread?.messages ?? [];
        for (let i = msgs.length - 1; i >= 0; i--) {
            if (msgs[i].direction === "inbound") return msgs[i].created_at;
        }
        return null;
    })();

    // #3242 (BE ourteam-backend#3981): HR "Trigger Step" — needs a flow-managed
    // candidate with a current step and a readable WhatsApp thread.
    // `pending_hr_trigger` = armed trigger awaiting the candidate's consent
    // reply; the BE caps the re-engage template at 1×/24h, so re-send stays
    // disabled until that window passes.
    const pendingHrTrigger = whatsappThread?.pending_hr_trigger ?? null;
    // Available while the candidate is working through the flow (Assessment In
    // Progress) AND at Ready for Review.
    //
    // Ready for Review used to be excluded on the reasoning that the ball is
    // with HR there, so there is nothing left to ask. That is backwards: it is
    // exactly while reviewing that HR finds the gap worth chasing — a vague
    // salary answer, a missing document, a general follow-up before deciding.
    // The step picker already handles a finished flow (every step reads
    // "Completed", nothing is preselected, and Send stays disabled until HR
    // picks one), so re-asking a completed step is a deliberate choice, not an
    // accident.
    //
    // Offers and Closed stay excluded — past the decision, this is no longer
    // the right channel.
    //
    // The `current_step` requirement is conditional for the same reason: the
    // flow is finished at Ready for Review, so `current_step` is null there
    // (see flowCurrentIndex / flowIsDone above). Requiring it would keep the
    // button hidden on the very stage this enables.
    const canTriggerStep = hasHiringFlow
        && !!applicant?.id
        && hiringFlowSteps.length > 0
        && !["offers", "closed"].includes(activeFunnelStage || "")
        && (!!applicant?.current_step || activeFunnelStage === "to_review");
    const triggerResendLimited = !!pendingHrTrigger
        && new Date().getTime() - new Date(pendingHrTrigger.armed_at).getTime() < 24 * 60 * 60 * 1000;

    // Result feedback per trigger mode. A dispatch that didn't send must never
    // read as success — surface the BE reason verbatim instead. The thread
    // refetch renders the template bubble / Maya's ask and, out-of-window, the
    // pending-consent banner.
    const handleStepTriggered = (result: TriggerStepResult, stepLabelText: string) => {
        if (result.dispatch && result.dispatch.sent === false) {
            snackbar.showError(`WhatsApp template was not sent: ${result.dispatch.reason || "unknown reason"}.`);
        } else if (result.mode === "asked_in_session") {
            snackbar.showSuccess(`Maya asked "${stepLabelText}" in the chat.`);
        } else if (result.mode === "consent_template_sent") {
            snackbar.showSuccess("Re-engage template sent — waiting for the candidate's reply.");
        } else {
            snackbar.showSuccess("Step invitation template re-sent.");
        }
        setWhatsappReloadKey((k) => k + 1);
    };

    // Which assessment sub-tabs are available for this candidate (same
    // conditions the old top-level tabs used). The "Assessment" top tab shows
    // when at least one is available.
    const showAiInterviewSub = !!job && !["Upload Complete", "CV Parsed"].includes(applicant_status);
    const showFormSub = !!(job && formDisplay);
    // Eligibility answers can come from the dedicated endpoint OR ride on the
    // applicants-list row itself (`applicant.elligibility_answers` — the same
    // source the score-breakdown dialog uses). WhatsApp-first in-chat candidates
    // may 404 on the endpoint while the row already carries their answers, so
    // fall back to the row payload instead of hiding the tab.
    const eligibilityAnswers = eligibilityCheck?.elligibility_answers ?? applicant?.elligibility_answers ?? null;
    const showEligibilitySub = !!(job && eligibilityAnswers);
    const assessmentSubTabs = [
        showAiInterviewSub && { key: "ai-interview", label: "AI Interview" },
        showFormSub && { key: "form", label: "Form" },
        showEligibilitySub && { key: "eligibility-check", label: "Eligibility Check" },
    ].filter(Boolean) as { key: string; label: string }[];
    const showAssessmentTab = assessmentSubTabs.length > 0;
    // Fall back to the first available sub-tab if the selected one isn't shown.
    const effectiveAssessmentSubTab =
        assessmentSubTabs.find((s) => s.key === assessmentSubTab)?.key ??
        assessmentSubTabs[0]?.key ??
        "ai-interview";

    // CV tab sub-tabs (#3146): merge CV + Documents under one tab (like Screening's
    // sub-tabs) when hiring flow is on; otherwise the CV tab has no switcher.
    const cvSubTabs = showDocumentsTab
        ? [{ key: "cv", label: "CV" }, { key: "documents", label: "Documents" }]
        : [{ key: "cv", label: "CV" }];
    const effectiveCvSubTab = cvSubTabs.find((s) => s.key === cvSubTab)?.key ?? "cv";

    useEffect(() => {
        if (user && activeTab === "cv") {
            getApplicantDisplayCV(user?.id || "").then((resp) => {
                if (resp.status === 200) {
                    setCvDisplay(resp.data.data);
                }
            })
        }
    }, [user, activeTab])

    // Reset scroll position when drawer opens or tab changes
    useEffect(() => {
        if (activeTab !== "review") {
            setActiveTabReview("applicant");
        }
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
        // Reset active section when tab changes
        if (activeTab === "profile") {
            setActiveSection("");
        }
    }, [isOpen, activeTab]);

    useEffect(() => {
        if (!isLoadingUserInfo && currentUserInfo && !user) {
            const savedCandidates = getCookie(`saved_candidate:${currentUserInfo?.id}`);
            if (savedCandidates) {
                const candidateId = applicant?.applicant_id || "";
                const exists = savedCandidates.find((item: string) => item === candidateId);
                setIsSaved(!!exists);
            } else {
                setIsSaved(false);
            }
        }
    }, [isLoadingUserInfo, currentUserInfo, user, reloadSavedBookmark])

    useEffect(() => {
        // Gate on the candidate's identity (user.id), NOT on parsed CV data.
        // CV-less hiring-flow applicants (auto-start / CV-optional entry) arrive
        // with `candidate` (cv_data) undefined but a valid user.id (= applicant_id),
        // so gating on `candidate` skipped their interview submission / video /
        // insights entirely. user.id is what every fetch below actually keys on.
        if (user?.id) {
            // Reset states
            setLoadingVideo(true);
            setLoadingInterviewInsight(true);
            setVideoInterview(undefined);
            setInterviewInsights(null);

            getListJobApplicant(user?.id as string).then((resp) => {
                if (resp.status === 200) {
                    setJobApplicantItems(resp.data.data);
                }
            })

            // Check if we have both job and user to fetch job-specific interview
            if (job?.assessment_ids && job?.assessment_ids.filter(assessment => assessment.type === "Interview").length > 0 && user?.id) {
                // First try to get user's submission for this specific job
                getUserInterviewSubmission(job.assessment_ids?.filter(assessment => assessment.type === "Interview")[0].id as string, user.id)
                    .then((submission: UserInterview) => {
                        if (submission && submission.conversation_id) {
                            // User has a submission for this job, get the specific video
                            console.log("Found interview submission for job:", job.id, "conversation ID:", submission.conversation_id, "status:", submission.status);

                            setInterviewStatus({
                                status: submission.status || interviewStatusNone,
                                reason: "",
                                updated_at: submission.updated_at || "",
                            })

                            // Get the video using the conversation ID
                            getVideo(submission.conversation_id, undefined, user?.id)
                                .then((resp) => {
                                    if (resp) {
                                        setVideoInterview(resp);
                                    }
                                    setLoadingVideo(false);
                                })
                                .catch((error) => {
                                    console.error("Failed to get job-specific video interview", error);
                                    setLoadingVideo(false);
                                });

                            // Get the insights using the conversation ID
                            getInterviewInsights(submission.conversation_id, undefined, user?.id)
                                .then((res) => {
                                    if (res?.insights) {
                                        setInterviewInsights(JSON.parse(res.insights));
                                    }
                                    setLoadingInterviewInsight(false);
                                })
                                .catch((error) => {
                                    console.error("Failed to get job-specific interview insights", error);
                                    setLoadingInterviewInsight(false);
                                });
                        } else {
                            // No job-specific submission found, fall back to general interview
                            console.log("No job-specific interview found, falling back to general interview");
                            fallbackToGeneralInterview();
                        }
                    })
                    .catch((error) => {
                        console.error("Failed to get user interview submission", error);
                        fallbackToGeneralInterview();
                    });
            } else {
                // No job or user ID, use the general interview
                fallbackToGeneralInterview();
            }
        }

        // Helper function for the fallback case to avoid code duplication
        function fallbackToGeneralInterview() {
            getCandidateInterviewStatus(user?.id || "")
                .then((resp) => {
                    // A candidate with no interview history has nothing to show, and the
                    // BE can return a blank/absent status for them. Treat "no submission"
                    // exactly like Pending Interview so the Screening tab reads "Pending"
                    // (not a blank badge) and hides the insights/video sections (#3131).
                    // Mirrors the job-specific path's `submission.status || …` default.
                    const data = resp.data;
                    setInterviewStatus({
                        ...data,
                        status: data?.status || interviewStatusPendingInterview,
                    });
                })
                .catch((error) => {
                    // An API failure must not leave the card blank either — default to
                    // Pending Interview (#3131).
                    console.error("Failed to get general interview status", error);
                    setInterviewStatus({ status: interviewStatusPendingInterview, reason: "", updated_at: "" });
                });

            getVideo(undefined, "First Time Interview", user?.id)
                .then((resp) => {
                    if (resp) {
                        setVideoInterview(resp);
                    }
                    setLoadingVideo(false);
                })
                .catch((error) => {
                    console.error("Failed to get general video interview", error);
                    setLoadingVideo(false);
                });

            getInterviewInsights(undefined, "First Time Interview", user?.id || "")
                .then((res) => {
                    if (res?.insights) {
                        setInterviewInsights(JSON.parse(res.insights));
                    }
                    setLoadingInterviewInsight(false);
                })
                .catch((error) => {
                    console.error("Failed to get general interview insights", error);
                    setLoadingInterviewInsight(false);
                });
        }
    }, [candidate, job, user])

    useEffect(() => {
        if (applicant && job && activeTab === "activity-history") {
            getCandidateActivityHistories(job.id, applicant.applicant_id).then((resp) => {
                if (resp.status === 200) {
                    const sortedGroups = [...resp.data.data].sort((a, b) => {
                        return new Date(b.date).getTime() - new Date(a.date).getTime();
                    });
                    setCandidateActivityGroups(sortedGroups);
                }
            })
        }
    }, [applicant, job, activeTab])

    // Scroll to section when activeSection changes
    useEffect(() => {
        if (
            activeTab === "profile" &&
            activeSection &&
            sectionRefs.current[activeSection]
        ) {
            const sectionElement = sectionRefs.current[activeSection];
            if (sectionElement) {
                // Offset by the sticky header's actual height (identity + tab nav)
                // plus a small gap, so the section lands just below it.
                const stickyOffset = (stickyHeaderRef.current?.offsetHeight ?? 100) + 12;
                contentRef.current?.scrollTo({
                    top: sectionElement.offsetTop - stickyOffset,
                    behavior: "smooth",
                });
            }
        }
    }, [activeSection, activeTab]);

    const scrollToSection = (section: string) => {
        setActiveSection(section);
    };

    const handleDrawerPost = () => {
        const content = drawerComposerText.trim();
        if (!content || isDrawerComposerPosting || !applicant?.id) return;
        setIsDrawerComposerPosting(true);
        createApplicationComment(applicant.id, { content })
            .then((resp) => {
                if (resp.status === 201 || resp.status === 200) {
                    setDrawerComposerText("");
                    snackbar.showSuccess("Comment posted.");
                    setCommentRefreshKey((prev) => prev + 1);
                }
            })
            .catch((err) => {
                catchErrorLog(`Failed to post comment: ${err}`);
                snackbar.showError(err?.response?.data?.message || "Failed to post comment.");
            })
            .finally(() => setIsDrawerComposerPosting(false));
    };

    const handleDownloadCV = async () => {
        if (!cvDisplay?.cv_url || !cvDisplay?.filename) return;

        try {
            const response = await fetch(cvDisplay.cv_url);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = cvDisplay.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download CV:', error);
            // Fallback to opening in new tab if download fails
            window.open(cvDisplay.cv_url, "_blank");
        }
    };

    const handleShare = () => {
        const base = `${getSubdomainHost(currentUserInfo?.company_subdomain || "")}/candidate/${cvID}`;
        const candidateProfileLink = job?.id ? `${base}?job_id=${job.id}` : base;
        navigator.clipboard
            .writeText(candidateProfileLink)
            .then(() => {
                snackbar.showSuccess("Candidate profile link copied to clipboard!");
            })
            .catch((err) => {
                console.error("Could not copy text: ", err);
            });
    };

    const handleBookmark = () => {
        setIsSaved(!isSaved);

        if (!isSaved) {
            bookmarkingCandidate(user?.id || "")
                .then((resp) => {
                    if (resp.status === 200) {
                        snackbar.showSuccess("Added to your Saved Candidates.");
                        setCookie(`saved_candidate:${currentUserInfo?.id}`, JSON.stringify(resp.data.data));
                    }
                }).catch((error) => {
                    catchErrorLog(`failed to bookmark candidate: ${error}`);
                    snackbar.showError("Failed to bookmark candidate.");
                }).finally(() => {
                    if (onReloadSavedBookmark) {
                        onReloadSavedBookmark();
                    }
                })
        } else {
            removeBookmarkCandidate(user?.id || "")
                .then((resp) => {
                    if (resp.status === 200) {
                        snackbar.showSuccess("Removed from your Saved Candidates.");
                        setCookie(`saved_candidate:${currentUserInfo?.id}`, JSON.stringify(resp.data.data));
                    }
                }).catch((error) => {
                    catchErrorLog(`failed to remove bookmark candidate: ${error}`);
                    snackbar.showError("Failed to remove candidate from bookmarks.");
                }).finally(() => {
                    if (onReloadSavedBookmark) {
                        onReloadSavedBookmark();
                    }
                });
        }
    }

    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    const handleInviteConfirm = async (data: InviteToApplyRequest) => {
        setLoadingInvitation(true)
        try {
            const resp = await inviteToApply(data)
            if (resp.code === 201) {
                setSuccessInvitation(true);
                snackbar.showSuccess(resp.message || "Invitation sent successfully");
            } else {
                snackbar.showError(resp.message || "")
            }
        } catch (error) {
            if (error instanceof Error) {
                console.error("failed to invite to apply", error.message);
            } else {
                console.error("failed to invite to apply", error);
            }
        } finally {
            if (onReload) {
                onReload();
                onClose();
            }
            setLoadingInvitation(false)
        }
    };

    const handleSendJobOffer = async (body: CreateContractRequest) => {
        createContractV2(body).then((resp) => {
            if (resp.status === 201) {
                snackbar.showSuccess('Job offer(s) sent successfully.');
                if (onReload) {
                    onReload();
                }
            } else {
                snackbar.showError(resp.data.message);
            }
        }).catch((error) => {
            catchErrorLog(`Failed to send job offer: ${error}`);
            snackbar.showError("Failed to send screening invitation. Please try again.");
        }).finally(() => {
            setShowJobOfferDialog(false);
            if (onReload) {
                onReload();
                onClose();
            }
        });
    };

    const handleSendInterviewInvitation = async (body: SendCustomizedEmailRequest, reviewScheduledNotes: ReviewScheduledNoteItem) => {
        sendCustomizedEmailV2({
            ...body,
            review_schedule_notes: reviewScheduledNotes,
        }).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess("Review invitation successfully sent.");
            }
        }).catch((error) => {
            catchErrorLog(`Failed to send interview invitation: ${error}`);
            if (error.response?.status === 400 && (error.response?.data?.is_usage_limit_reached || error.response?.data?.is_trial_end_soon_usage_reached)) {
                showTrialUsageLimitDialog(!!error.response?.data?.is_trial_end_soon_usage_reached);
            } else if (error.response?.data?.message) {
                snackbar.showError(error.response.data.message);
            }
        }).finally(() => {
            setShowInterviewInvitationDialog(false);
            if (onReload) {
                onReload();
                onClose();
            }
        });
    };

    const handleRejectCandidate = async (body: SendCustomizedEmailRequest) => {
        sendCustomizedEmailV2(body).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess(resp.data.message);
            }
        }).catch((error) => {
            catchErrorLog(`Failed to reject candidate: ${error}`);
            if (error.response?.status === 400 && (error.response?.data?.is_usage_limit_reached || error.response?.data?.is_trial_end_soon_usage_reached)) {
                showTrialUsageLimitDialog(!!error.response?.data?.is_trial_end_soon_usage_reached);
            } else if (error.response?.data?.message) {
                snackbar.showError(error.response.data.message);
            }
        }).finally(() => {
            setShowRejectOfferDialog(false);
            if (onReload) {
                onReload();
                onClose();
            }
        });
    };

    const handleApplicantStatusBadge = (newStatus: string) => {
        if (newStatus === "review_scheduled") {
            setReviewScheduleManagerDialogOpen(true);
            return;
        }

        applicantUpdateStatus(job?.id || "", {
            user_id: applicant?.applicant_id || "",
            status: convertCapitalizeWords(newStatus),
        }).then((resp) => {
            if (resp.status === 200) {
                setApplicantNewStatus(newStatus);
                snackbar.showSuccess(`Candidate status updated to ${convertCapitalizeWords(newStatus)}`);
                onClose();
            }
        }).catch((error) => {
            catchErrorLog(`failed to update applicant status badge: ${error}`);
            snackbar.showError("Failed to update applicant status.");
        }).finally(() => {
            if (onReload) {
                onReload();
            }
        })
    }

    const handleMarkSendOffer = (applicant_ids: string[], note: string) => {
        applicantUpdateStatus(job?.id || "", {
            user_ids: applicant_ids,
            status: convertCapitalizeWords(isMarkOfferResent ? "offer_resent" : "offer_sent"),
            offer_notes: note,
        }).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess(`Status updated to ${convertCapitalizeWords(isMarkOfferResent ? "offer_resent" : "offer_sent")}`);
                if (onReload) {
                    onReload();
                }
            }
        }).finally(() => {
            setIsMarkOfferResent(false);
        }).catch((error) => {
            catchErrorLog(`failed to update applicant status badge: ${error}`);
            snackbar.showError("Failed to update applicant status to offer sent. Please try again.");
        })
    }

    const onMarkApplicantStatus = (applicant_id: string, status: string) => {
        if (status === "review_scheduled") {
            setReviewScheduleManagerDialogOpen(true);
            return;
        }

        applicantUpdateStatus(job?.id || "", {
            user_ids: [applicant_id],
            status: convertCapitalizeWords(status),
        }).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess(`Status updated to ${convertCapitalizeWords(status)}`);
                if (onReload) {
                    onReload();
                }
                onClose();
            }
        }).catch((error) => {
            catchErrorLog(`failed to update applicant status badge: ${error}`);
            snackbar.showError("Failed to update applicant status. Please try again.");
        })
    }

    // #3186: resolve a hiring-flow gate flag from the drawer footer — mirrors the
    // per-row actions in applicant-item.tsx (uses the application id, reloads on success).
    const handleFlowResolve = (resolution: FlowGateResolution, successMessage: string) => {
        if (!applicant?.id) return;
        setFlowActionLoading(true);
        resolveFlowGate(applicant.id, resolution)
            .then(() => {
                snackbar.showSuccess(successMessage);
                onReload?.();
                onClose();
            })
            .catch((error) => {
                catchErrorLog(`failed to resolve flow gate: ${error}`);
                snackbar.showError("Failed to resolve the flag. Please try again.");
            })
            .finally(() => setFlowActionLoading(false));
    };

    // #3297 (BE ourteam-backend#4031): finalize a stuck all-steps-completed
    // candidate into Ready for Review via the ordinary flow/advance call.
    const handleMoveToReview = () => {
        if (!applicant?.id) return;
        setFlowActionLoading(true);
        advanceApplicationFlow(applicant.id)
            .then(() => {
                snackbar.showSuccess("Candidate moved to Ready for Review.");
                onReload?.();
                onClose();
            })
            .catch((error) => {
                const beMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
                catchErrorLog(`failed to finalize flow: ${error}`);
                if (isClientTimeout(error)) {
                    // Idempotent on the BE — the finalize may well have landed.
                    // Refresh and leave the drawer open on the real state rather
                    // than closing it behind a "failed" that may be untrue.
                    snackbar.showError(FLOW_TIMEOUT_TITLE, FLOW_TIMEOUT_HINT);
                    onReload?.();
                    return;
                }
                snackbar.showError(beMessage || "Failed to move the candidate. Please try again.");
            })
            .finally(() => setFlowActionLoading(false));
    };

    // #3250 (BE ourteam-backend#3992): approve a pending call-retake request.
    // The ordinary flow/advance call doubles as the approval; idempotent on the
    // BE, so a double submit cannot reopen the interview twice.
    const handleApproveRetake = () => {
        if (!applicant?.id) return;
        setFlowActionLoading(true);
        advanceApplicationFlow(applicant.id)
            .then(() => {
                snackbar.showSuccess("Retake approved — the interview is reopened for the candidate.");
                onReload?.();
                onClose();
            })
            .catch((error) => {
                catchErrorLog(`failed to approve retake: ${error}`);
                if (isClientTimeout(error)) {
                    snackbar.showError(FLOW_TIMEOUT_TITLE, FLOW_TIMEOUT_HINT);
                    onReload?.();
                    return;
                }
                snackbar.showError("Failed to approve the retake. Please try again.");
            })
            .finally(() => setFlowActionLoading(false));
    };

    // #3186/#3877: reject a flagged candidate — email dialog when they have an
    // email, WhatsApp reject dialog when they don't (WhatsApp-first / CV-less).
    const handleDrawerRejectFlagged = () => {
        if (hasNoEmail) {
            setShowWaRejectDialog(true);
        } else {
            setShowRejectOfferDialog(true);
        }
    };

    const handleSendAssessmentInvitation = (body: SendCustomizedEmailRequest, type: string) => {
        sendCustomizedEmailV2(body).then((resp) => {
            if (resp.status === 200) {
                return applicantUpdateStatus(job?.id || "", {
                    user_ids: body.user_ids || [],
                    status: "Pending Assessment",
                }).then((resp) => {
                    if (resp.status === 200) {
                        if (type === "reminder") {
                            snackbar.showSuccess("Screening reminder successfully sent.");
                        } else if (type === "resend-invitation") {
                            snackbar.showSuccess("Screening link successfully sent.");
                        } else if (type === "retake") {
                            snackbar.showSuccess("Retake interview request sent successfully.");
                        } else {
                            snackbar.showSuccess("Screening invitation successfully sent.");
                        }

                        if (onReload) {
                            onReload();
                        }
                    }
                }).catch((error) => {
                    catchErrorLog(`failed to update applicant status badge: ${error}`);
                    snackbar.showError("Failed to send screening invitation. Please try again.");
                })
            }
        }).catch((error) => {
            catchErrorLog(`Failed to send assessment invitation: ${error}`);
            if (error.response?.status === 400 && (error.response?.data?.is_usage_limit_reached || error.response?.data?.is_trial_end_soon_usage_reached)) {
                showTrialUsageLimitDialog(!!error.response?.data?.is_trial_end_soon_usage_reached);
            } else if (error.response?.data?.message) {
                snackbar.showError(error.response.data.message);
            } else {
                snackbar.showError("Failed to send screening invitation. Please try again.");
            }
        }).finally(() => {
            setShowAssessmentInvitationDialog(false);
            setShowAssessmentInvitationReminderDialog(false);
            setShowAssessmentRetakeDialog(false);
            setShowAssessmentResendDialog(false);
        });
    }

    const isShortlisted = shortlistedItems.some((item) => item.collaborator_id === currentUserInfo?.id);

    return (
        <>
            <div
                className={cn(
                    "fixed inset-y-0 right-0 z-50 bg-white dark:bg-gray-900 border-l transform transition-all duration-300 ease-in-out flex flex-col",
                    isOpen ? "translate-x-0" : "translate-x-full",
                    isFullScreen ? "w-full" : "w-full sm:w-[600px] md:w-[700px] lg:w-1/2"
                )}
            >
                {/* Header with close button */}
                <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 p-4">
                    <h2 className="text-lg font-semibold">Candidate Profile</h2>
                    <div className="flex items-center space-x-2">
                        <Button
                            variant="ghost"
                            onClick={() => setShortlistDialogOpen(true)}
                            className="h-8 gap-1 px-2"
                        >
                            <HeartIcon className={cn("w-4 h-4", {
                                "text-pink-500 fill-pink-500": isShortlisted,
                            })} />
                            {countShortlisted > 0 && (
                                <span className="text-sm text-gray-600 dark:text-gray-400">{countShortlisted}</span>
                            )}
                        </Button>
                        <span className="w-[1px] h-[20px] border-l"></span>
                        {currentUserInfo?.permission !== "member" && (
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleBookmark}
                                className="h-8 w-8"
                                title={isSaved ? "Remove from bookmarks" : "Add to bookmarks"}
                            >
                                <Bookmark
                                    className={cn(
                                        "h-4 w-4",
                                        isSaved && "fill-current text-blue-600 dark:text-blue-400"
                                    )}
                                />
                            </Button>
                        )}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleShare}
                            className="h-8 w-8"
                            title="Share profile"
                        >
                            <Share2 className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleFullScreen}
                            className="h-8 w-8 sm:flex hidden"
                            title={isFullScreen ? "Exit full screen" : "Full screen"}
                        >
                            {isFullScreen ? (
                                <Minimize2 className="h-4 w-4" />
                            ) : (
                                <Maximize2 className="h-4 w-4" />
                            )}
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onClose}
                            className="h-8 w-8"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {/* Main content area */}
                <div
                    className={cn(
                        "flex-1",
                        // Messages tab: make the content region a flex column that
                        // fills the drawer height, so the chat scrolls inside itself
                        // instead of adding a second scrollbar over the drawer.
                        activeTab === "messages" ? "flex flex-col overflow-hidden" : "overflow-y-auto"
                    )}
                    ref={contentRef}
                >
                    {/* Sticky header — candidate identity + tab nav stay pinned to
                        the top of the scroll area while the tab content scrolls. */}
                    <div ref={stickyHeaderRef} className="sticky top-0 z-20 bg-white dark:bg-gray-900 flex-shrink-0">
                    {/* Candidate header with avatar and name */}
                    <div className="p-6 pb-0">
                        <div className="flex items-center space-x-4">
                            <div className="w-full">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-xl font-semibold">{displayCandidateName || "—"}</h3>
                                    {job && activeFunnelStage && (
                                        <div className="flex flex-col items-end">
                                            <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap ml-4">
                                                Stage: {" "}
                                                <span className="font-medium">
                                                    {activeFunnelStage === "uploads" && "Uploads"}
                                                    {activeFunnelStage === "applied" && "Applied"}
                                                    {activeFunnelStage === "pending_assessment" && "Screening in Progress"}
                                                    {activeFunnelStage === "to_review" && "Ready to Review"}
                                                    {activeFunnelStage === "offers" && "Offers"}
                                                    {activeFunnelStage === "closed" && "Closed"}
                                                </span>
                                            </span>
                                            <p className="text-xs text-gray-500">Applied to: {job.job_title}</p>
                                            <p className="text-xs text-gray-500">Applied: {parseFormat(applicant?.created_at || "", "D MMMM YYYY")}</p>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center text-gray-500 dark:text-gray-400">
                                    <Building2 className="h-4 w-4 mr-1" />
                                    <span>{candidate?.CV_data?.position || "—"}</span>
                                </div>
                                {isShowPercentMatch && (
                                    <>
                                        <div className="flex flex-row mt-2 space-x-3">
                                            {!isSuggestionCandidate && (
                                                <>
                                                    {job?.company_is_byoc ? (
                                                        <DropdownApplicantStatus
                                                            isDisabledPopover
                                                            isStaticDisplay
                                                            currentStatus={convertToLowerSnakeCase(applicant?.status || applicant_status)}
                                                            onStatusChange={handleApplicantStatusBadge}
                                                            isOffersTab={activeFunnelStage === "offers"}
                                                        />
                                                    ) : (
                                                        <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${getApplicationStatusClass(applicant_status === applicationStatusMap.invitedFUSent ? applicationStatusMap.invitationExpired : applicant_status)}`}>
                                                            {applicant_status === applicationStatusMap.invitedFUSent ? applicationStatusMap.invitationExpired : applicant_status}
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                            <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${getMatchBadgeClasses(similarity_score || 0)}`}>
                                                {Number(similarity_score) || 0}% match
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        {isSuggestionCandidate && applicantNewStatus && (applicantNewStatus !== applicationStatusMap.notInvited && applicantNewStatus !== applicationStatusMap.invitedFUSent) && (
                            <div
                                className={`mt-4 mb-4 p-3 ${getInvitationBannerClass(applicantNewStatus)} ${getInvitationBorderClass(applicantNewStatus)} border rounded-md flex items-center gap-2`}
                            >
                                <div className="flex-1">
                                    <div className="flex flex-row items-center space-x-2">
                                        {applicantNewStatus === applicationStatusMap.invitedToApply ? (
                                            <CheckCircleIcon className="h-4 w-4" />
                                        ) : applicantNewStatus === applicationStatusMap.invitationExpired ? (
                                            <AlertCircleIcon className="h-4 w-4" />
                                        ) : null}
                                        <p
                                            className={`text-sm font-medium`}
                                        >
                                            {applicantNewStatus === applicationStatusMap.invitedFUSent ? applicationStatusMap.invitationExpired : applicantNewStatus}
                                        </p>
                                    </div>
                                    <p className="text-xs ml-6">
                                        {parseFormat(applicant?.invitation_date || "", "D MMM YYYY HH:mm A")}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Compact hiring-flow stepper (#3076) — glance level; identical
                        data to the Flow tab. Done ✓ · flagged ⚑ · current · upcoming.
                        Clicking a step jumps to the Flow tab. */}
                    {showFlowSurface && hasHiringFlow && hiringFlowSteps.length > 0 && (
                        <div className="px-6 mt-4">
                            <div className="flex items-center flex-wrap gap-y-1.5">
                                {hiringFlowSteps.map((step, i) => {
                                    const stepNo = i + 1;
                                    const isDone = flowIsDone || (flowCurrentIndex !== null && stepNo < flowCurrentIndex);
                                    const isCurrent = !flowIsDone && flowCurrentIndex === stepNo;
                                    const isFlagged = isCurrent && !!applicant?.paused_at;
                                    return (
                                        <div key={step.id || i} className="flex items-center">
                                            <button
                                                type="button"
                                                onClick={() => setActiveTab("flow")}
                                                className="flex items-center gap-1.5 group"
                                                title={flowStepLabel(step)}
                                            >
                                                <span className={cn(
                                                    "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0",
                                                    isFlagged ? "bg-amber-100 text-amber-700"
                                                        : isDone ? "bg-green-100 text-green-700"
                                                            : isCurrent ? "bg-blue-600 text-white"
                                                                : "bg-gray-100 text-gray-500"
                                                )}>
                                                    {isFlagged ? <FlagIcon className="w-2.5 h-2.5" /> : isDone ? <CheckIcon className="w-3 h-3" /> : stepNo}
                                                </span>
                                                <span className={cn(
                                                    "text-xs whitespace-nowrap group-hover:text-blue-600",
                                                    isFlagged ? "text-amber-700 font-medium"
                                                        : isCurrent ? "text-blue-600 font-medium"
                                                            : isDone ? "text-gray-700" : "text-gray-400"
                                                )}>
                                                    {flowStepLabel(step)}
                                                </span>
                                            </button>
                                            {i < hiringFlowSteps.length - 1 && (
                                                <div className="w-4 h-px bg-gray-200 mx-1.5 flex-shrink-0" />
                                            )}
                                        </div>
                                    );
                                })}
                                {flowPinnedVersion !== null && (
                                    <span className="ml-2 text-[10px] font-medium text-gray-400">flow v{flowPinnedVersion}</span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Maya escalation banner (#3091) — Maya handed the WhatsApp
                        thread off; HR needs to step in. Clears once HR replies
                        (BE hr_read signal). */}
                    {applicant?.maya_escalation?.needs_reply && (
                        <div className="mx-6 mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                            <MessageSquareIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <b>Maya escalated — reply needed.</b>{" "}
                                {applicant.maya_escalation.reason || "Maya handed this conversation off to you."}
                            </div>
                            {mayaEnabled && candidateWhatsappId && (
                                <button
                                    type="button"
                                    onClick={() => setActiveTab("messages")}
                                    className="flex-shrink-0 text-xs font-medium text-amber-800 underline hover:text-amber-900"
                                >
                                    Open messages
                                </button>
                            )}
                        </div>
                    )}

                    {/* Pending call-retake banner (#3250, BE ourteam-backend#3992) —
                        the candidate asked Maya to redo the phone interview; the
                        retake only happens once HR approves it here (or on the row). */}
                    {applicant?.retake_request && (
                        <div className="mx-6 mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                            <RotateCcwIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <b>Call retake requested — {applicant.retake_request.step_label}.</b>{" "}
                                Asked {parseFormat(applicant.retake_request.requested_at, "D MMM YYYY h:mm A")}.
                                {applicant.retake_request.reason && (
                                    <span className="block text-amber-700">“{applicant.retake_request.reason}”</span>
                                )}
                            </div>
                            <Button
                                size="sm"
                                disabled={isFlowActionLoading}
                                className="flex-shrink-0 h-7 bg-blue-600 hover:bg-blue-700 text-white text-xs px-3"
                                onClick={handleApproveRetake}
                            >
                                {isFlowActionLoading ? "Approving…" : "Approve retake"}
                            </Button>
                        </div>
                    )}

                    {/* Tab navigation */}
                    <div className="border-b border-gray-200 dark:border-gray-800 mt-6">
                        <div className="flex px-6">
                            <button
                                onClick={() => setActiveTab("profile")}
                                className={cn(
                                    "py-2 px-4 text-sm font-medium relative flex items-center gap-1.5",
                                    activeTab === "profile"
                                        ? "text-blue-600 dark:text-blue-400"
                                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                )}
                            >
                                <UserIcon className="w-3.5 h-3.5" />
                                Profile
                                {activeTab === "profile" && (
                                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400"></div>
                                )}
                            </button>
                            <button
                                onClick={() => setActiveTab("cv")}
                                className={cn(
                                    "py-2 px-4 text-sm font-medium relative flex items-center gap-1.5",
                                    activeTab === "cv"
                                        ? "text-blue-600 dark:text-blue-400"
                                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                )}
                            >
                                <FileIcon className="w-3.5 h-3.5" />
                                CV
                                {activeTab === "cv" && (
                                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400"></div>
                                )}
                            </button>
                            {/* Assessment — merges AI Interview, Form and Eligibility Check
                                into one tab with sub-tabs (see Tab content). */}
                            {showAssessmentTab && (
                                <button
                                    onClick={() => setActiveTab("assessment")}
                                    className={cn(
                                        "py-2 px-4 text-sm font-medium relative flex items-center gap-1.5",
                                        activeTab === "assessment"
                                            ? "text-blue-600 dark:text-blue-400"
                                            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                    )}
                                >
                                    <ClipboardListIcon className="w-3.5 h-3.5" />
                                    Screening
                                    {activeTab === "assessment" && (
                                        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400"></div>
                                    )}
                                </button>
                            )}
                            {/* Flow tab (#3076) — detail level: full step timeline with
                                gate events. Custom-flow candidates only. Hidden per
                                product request (see showFlowSurface). */}
                            {showFlowSurface && hasHiringFlow && (
                                <button
                                    onClick={() => setActiveTab("flow")}
                                    className={cn(
                                        "py-2 px-4 text-sm font-medium relative flex items-center gap-1.5",
                                        activeTab === "flow"
                                            ? "text-blue-600 dark:text-blue-400"
                                            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                    )}
                                >
                                    <WorkflowIcon className="w-3.5 h-3.5" />
                                    Flow
                                    {activeTab === "flow" && (
                                        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400"></div>
                                    )}
                                </button>
                            )}
                            {mayaEnabled && candidateWhatsappId && (
                                <button
                                    onClick={() => {
                                        setActiveTab("messages");
                                        // The page-1 fetch on open already marked
                                        // these read server-side; clear the local cue.
                                        setWhatsappHasUnread(false);
                                    }}
                                    className={cn(
                                        "py-2 px-4 text-sm font-medium relative flex items-center gap-1.5",
                                        activeTab === "messages"
                                            ? "text-blue-600 dark:text-blue-400"
                                            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                    )}
                                >
                                    <MessageSquareIcon className="w-3.5 h-3.5" />
                                    Messages
                                    {whatsappHasUnread && activeTab !== "messages" && (
                                        <span
                                            className="w-2 h-2 rounded-full bg-blue-600 dark:bg-blue-400"
                                            title="New WhatsApp messages"
                                        />
                                    )}
                                    {activeTab === "messages" && (
                                        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400"></div>
                                    )}
                                </button>
                            )}
                            {(!(currentUserInfo?.job_collaborations || []).includes(job?.id || "") || currentUserInfo?.permission !== "member") && (
                                <button
                                    onClick={() => setActiveTab("review")}
                                    className={cn(
                                        "py-2 px-4 text-sm font-medium relative flex items-center gap-1.5", {
                                        "text-blue-600 dark:text-blue-400": activeTab === "review",
                                        "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300": activeTab !== "review",
                                        "hidden": currentUserInfo?.permission === "member",
                                    }
                                    )}
                                >
                                    <StarIcon className="w-3.5 h-3.5" />
                                    Review
                                    {activeTab === "review" && (
                                        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400"></div>
                                    )}
                                </button>
                            )}
                            {job && (
                                <button
                                    onClick={() => setActiveTab("activity-history")}
                                    className={cn(
                                        "py-2 px-4 text-sm font-medium relative flex items-center gap-1.5",
                                        activeTab === "activity-history"
                                            ? "text-blue-600 dark:text-blue-400"
                                            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                    )}
                                >
                                    <HistoryIcon className="w-3.5 h-3.5" />
                                    Activity Log
                                    {activeTab === "activity-history" && (
                                        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400"></div>
                                    )}
                                </button>
                            )}
                            {!job && (
                                <button
                                    onClick={() => setActiveTab("job-applications")}
                                    className={cn(
                                        "py-2 px-4 text-sm font-medium relative flex items-center gap-1.5",
                                        activeTab === "job-applications"
                                            ? "text-blue-600 dark:text-blue-400"
                                            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                    )}
                                >
                                    <BriefcaseIcon className="w-3.5 h-3.5" />
                                    Job Applications
                                    {activeTab === "job-applications" && (
                                        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400"></div>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                    </div>{/* /sticky header */}

                    {/* Tab content */}
                    <div className={cn("p-6", activeTab === "messages" && "flex-1 min-h-0 flex flex-col")}>
                        {activeTab === "profile" ? (
                            <ProfileTabContent
                                candidate={candidate}
                                activeSection={activeSection}
                                scrollToSection={scrollToSection}
                                sectionRefs={sectionRefs}
                            />
                        ) : activeTab === "cv" ? (
                            <div className="space-y-4">
                                {/* CV + Documents sub-tabs (#3146) — merged into one tab
                                    like Screening, ONLY when hiring flow is on. Disabled →
                                    plain CV as before. */}
                                {showDocumentsTab && (
                                    <div className="inline-flex items-center gap-1 rounded-lg bg-gray-100 dark:bg-gray-800 p-1">
                                        {cvSubTabs.map((st) => (
                                            <button
                                                key={st.key}
                                                type="button"
                                                onClick={() => setCvSubTab(st.key)}
                                                className={cn(
                                                    "px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap",
                                                    effectiveCvSubTab === st.key
                                                        ? "bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 shadow-sm"
                                                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                                )}
                                            >
                                                {st.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {showDocumentsTab && effectiveCvSubTab === "documents" ? (
                                    <FlowDocumentsTabContent
                                        applicationId={applicant?.id || ""}
                                        isActive={activeTab === "cv" && effectiveCvSubTab === "documents"}
                                    />
                                ) : (
                                <div className="h-full space-y-4">
                                    {cvDisplay?.filename || cvDisplay?.cv_url ? (
                                    <>
                                    {/* CV Details */}
                                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                                                <FileIcon className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
                                                CV Details
                                            </h4>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                disabled={!cvDisplay?.filename}
                                                onClick={handleDownloadCV}
                                                className="h-8 gap-2"
                                            >
                                                <Download className="h-4 w-4" />
                                                Download CV
                                            </Button>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="text-sm">
                                                <span className="text-gray-600 dark:text-gray-400">File Name:</span>
                                                <span className="text-gray-900 dark:text-gray-100 font-medium ml-2">
                                                    {cvDisplay?.filename || ""}
                                                </span>
                                            </div>
                                            <div className="text-sm">
                                                <span className="text-gray-600 dark:text-gray-400">Date Parsed:</span>
                                                <span className="text-gray-900 dark:text-gray-100 font-medium ml-2">
                                                    {cvDisplay?.uploaded_at ? parseFormat(cvDisplay.uploaded_at, "D MMMM YYYY") : ""}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* CV Preview */}
                                    {cvDisplay && (
                                        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg h-[600px]">
                                            <PDFViewer
                                                url={cvDisplay?.cv_url || ""}
                                                className="w-full h-full"
                                            />
                                        </div>
                                    )}
                                    </>
                                    ) : (
                                        /* No parsed CV — a clear empty state for CV-less flow candidates
                                           (auto-start / CV-optional entry leave cv_data undefined). */
                                        <div className="flex flex-col items-center justify-center text-center py-16 px-6 border border-dashed border-gray-200 dark:border-gray-800 rounded-lg bg-gray-50/50 dark:bg-gray-900/40">
                                            <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                                                <FileXIcon className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                                            </div>
                                            <h4 className="text-base font-medium text-gray-900 dark:text-white mb-1">
                                                No CV uploaded
                                            </h4>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
                                                This candidate didn't attach a CV — their profile is built from the application form and chat.
                                            </p>
                                        </div>
                                    )}
                                </div>
                                )}
                            </div>
                        ) : activeTab === "assessment" ? (
                            <div className="space-y-6">
                                {/* Sub-tab segmented control — only when there's more than
                                    one assessment type; a lone sub-tab needs no switcher. */}
                                {assessmentSubTabs.length > 1 && (
                                    <div className="inline-flex items-center gap-1 rounded-lg bg-gray-100 dark:bg-gray-800 p-1">
                                        {assessmentSubTabs.map((st) => (
                                            <button
                                                key={st.key}
                                                type="button"
                                                onClick={() => setAssessmentSubTab(st.key)}
                                                className={cn(
                                                    "px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap",
                                                    effectiveAssessmentSubTab === st.key
                                                        ? "bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 shadow-sm"
                                                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                                )}
                                            >
                                                {st.label}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {effectiveAssessmentSubTab === "ai-interview" ? (
                                    <AiInterviewTabContent
                                        interviewStatus={interviewStatus}
                                        interviewInsights={interviewInsights}
                                        isLoadingInterviewInsight={isLoadingInterviewInsight}
                                        videoDuration={videoDuration}
                                    />
                                ) : effectiveAssessmentSubTab === "form" ? (
                                    <FormTabContent formDisplay={formDisplay} />
                                ) : effectiveAssessmentSubTab === "eligibility-check" ? (
                                    /* Same presentation as the job-applications-list
                                       Eligibility Check view: read-only SWEExperienceSurvey. */
                                    <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900">
                                        <SWEExperienceSurvey
                                            is_view
                                            work_authorization_metadata={eligibilityCheck?.work_authorization_metadata ?? job?.work_authorization_metadata ?? undefined}
                                            elligility_answers={eligibilityAnswers ?? undefined}
                                            location_metadata={eligibilityCheck?.location_metadata ?? job?.location_metadata ?? undefined}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        ) : activeTab === "flow" ? (
                            /* Flow tab (#3076): full timeline — steps in order with
                               state + gate events (flags, waivers, extensions). */
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Hiring flow</h4>
                                    {flowPinnedVersion !== null && (
                                        <span className="text-xs text-gray-400">Candidate is on flow v{flowPinnedVersion}</span>
                                    )}
                                </div>
                                {hiringFlowSteps.length === 0 ? (
                                    <p className="text-sm text-gray-500">
                                        The flow definition isn't available on this job read — progress:
                                        {" "}{applicant?.flow_progress ? `${applicant.flow_progress.completed} of ${applicant.flow_progress.total} steps completed` : "unknown"}.
                                    </p>
                                ) : (
                                    <div className="space-y-0">
                                        {hiringFlowSteps.map((step, i) => {
                                            const stepNo = i + 1;
                                            const isDone = flowIsDone || (flowCurrentIndex !== null && stepNo < flowCurrentIndex);
                                            const isCurrent = !flowIsDone && flowCurrentIndex === stepNo;
                                            const isFlagged = isCurrent && !!applicant?.paused_at;
                                            const events = flowGateEvents.filter((e) => e.step_id === step.id);
                                            return (
                                                <div key={step.id || i} className="flex gap-3">
                                                    <div className="flex flex-col items-center flex-shrink-0">
                                                        <span className={cn(
                                                            "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                                                            isFlagged ? "bg-amber-100 text-amber-700"
                                                                : isDone ? "bg-green-100 text-green-700"
                                                                    : isCurrent ? "bg-blue-600 text-white"
                                                                        : "bg-gray-100 text-gray-500"
                                                        )}>
                                                            {isFlagged ? <FlagIcon className="w-3 h-3" /> : isDone ? <CheckIcon className="w-3.5 h-3.5" /> : stepNo}
                                                        </span>
                                                        {i < hiringFlowSteps.length - 1 && (
                                                            <div className="w-px flex-1 bg-gray-200 dark:bg-gray-700 my-1" />
                                                        )}
                                                    </div>
                                                    <div className={cn("pb-5 min-w-0", i === hiringFlowSteps.length - 1 && "pb-0")}>
                                                        <p className={cn(
                                                            "text-sm font-medium",
                                                            isFlagged ? "text-amber-700" : isCurrent ? "text-blue-600" : isDone ? "text-gray-900 dark:text-gray-100" : "text-gray-400"
                                                        )}>
                                                            {flowStepLabel(step)}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {isFlagged
                                                                ? `Flagged — ${convertCapitalizeWords(convertToLowerSnakeCase(applicant?.status || ""))}${applicant?.flag_reason ? ` · ${applicant.flag_reason}` : ""}`
                                                                : isDone ? "Completed"
                                                                    : isCurrent ? "In progress"
                                                                        : "Upcoming"}
                                                        </p>
                                                        {events.map((event, k) => (
                                                            <p key={k} className="text-xs text-amber-700 mt-1">
                                                                ⚑ Condition · {event.gate_type === "deadline" ? "deadline" : "knockout"} — {event.result || "flagged"}
                                                                {event.resolution ? ` · resolved: ${typeof event.resolution === "string" ? event.resolution : event.resolution.action}` : " · awaiting HR"}
                                                                {event.at ? ` · ${parseFormat(event.at, "D MMM YYYY h:mm A")}` : ""}
                                                            </p>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ) : activeTab === "review" ? (
                            <ReviewTabContent
                                candidateId={user?.id || ""}
                                currentUserId={currentUserInfo?.id}
                                currentUserName={[currentUserInfo?.first_name, currentUserInfo?.last_name].filter(Boolean).join(" ").trim() || undefined}
                                currentUserPhoto={currentUserInfo?.profile_picture_path}
                                job={job}
                                isLoading={isLoadingShortlistedCandidate}
                                shortlistedCandidates={shortlistedCandidates}
                                metaPagination={metaPagination}
                                currentPage={currentPage}
                                reviewType={reviewType}
                                reviewCounts={reviewCounts}
                                onReviewTypeChange={setReviewType}
                                onSetCurrentPage={setCurrentPage}
                                onMarkAsFavourite={() => setShortlistDialogOpen(true)}
                                onReload={() => {
                                    const params: SearchShortlistedCandidateRequest = {
                                        page: currentPage,
                                        limit: defaultPagination.limit,
                                        candidate_id: applicant ? applicant.applicant_id : user?.id || "",
                                        view: job ? "applicant" : "candidate",
                                        job_id: job ? job.id : undefined,
                                        type: reviewType,
                                    };
                                    searchShortlistedCandidate(params).then((resp) => {
                                        if (resp.status === 200) {
                                            setShortlistedCandidates(resp.data.data);
                                            setMetaPagination(resp.data.meta.pagination);
                                        }
                                    });
                                    refreshReviewCounts();
                                }}
                                onDeleteReview={(shortlistId) => {
                                    const candidateId = applicant ? applicant.applicant_id : user?.id || "";
                                    deleteShortlistCandidate(shortlistId).then(() => {
                                        snackbar.showSuccess("Favourite note deleted for candidate.");
                                        const params: SearchShortlistedCandidateRequest = {
                                            page: currentPage,
                                            limit: defaultPagination.limit,
                                            candidate_id: candidateId,
                                            view: job ? "applicant" : "candidate",
                                            job_id: job ? job.id : undefined,
                                            type: reviewType,
                                        };
                                        searchShortlistedCandidate(params).then((resp) => {
                                            if (resp.status === 200) {
                                                setShortlistedCandidates(resp.data.data);
                                                setMetaPagination(resp.data.meta.pagination);
                                            }
                                        });
                                        refreshReviewCounts();
                                        // Refresh isShortlisted
                                        if (applicant?.applicant_id && job?.id) {
                                            getShortlistedByCandidateAndJobID(applicant.applicant_id, job.id).then((res) => {
                                                if (res.status === 200) {
                                                    const items = res.data.data || [];
                                                    setShortlistedItems(items);
                                                    setCountShortlisted(items.length);
                                                }
                                            });
                                        } else if (candidateId) {
                                            getShortlistedByCandidateID(candidateId).then((res) => {
                                                if (res.status === 200) {
                                                    const items = res.data.data || [];
                                                    setShortlistedItems(items);
                                                    setCountShortlisted(items.length);
                                                }
                                            });
                                        }
                                        if (onReloadSavedBookmark) {
                                            onReloadSavedBookmark();
                                        }
                                    }).catch((err) => {
                                        catchErrorLog(`Failed to delete shortlist: ${err}`);
                                    });
                                }}
                            />
                        ) : activeTab === "job-applications" ? (
                            <JobApplicationsList user_id={user?.id || ""} jobApplicantItems={jobApplicantItems} />
                        ) : activeTab === "messages" ? (
                            <MessagesTabContent
                                candidateId={candidateWhatsappId}
                                candidateName={displayCandidateName || undefined}
                                initialResult={whatsappThread}
                                initialError={whatsappThreadError}
                                isInitialLoading={whatsappThreadLoading}
                            />
                        ) : (
                            <ActivityLogTabContent activityGroups={candidateActivityGroups} />
                        )}

                        {/* InterviewPlayer is kept mounted (via isHide) so the audio/video
                            doesn't reload on tab switch. It sits OUTSIDE AiInterviewTabContent.
                            AiInterviewTabContent's own mb-6 is DEAD only when it's a NON-first
                            child of the assessment space-y-6 wrapper — i.e. when the sub-tab
                            switcher is shown (space-y-6 sets margin-bottom:0 on non-first
                            children, overriding mb-6). With NO switcher (a single assessment,
                            e.g. the Applied stage) AiInterviewTabContent is the first child, so
                            mb-6 is ALIVE (24px). So add pt-6 ONLY when the switcher shows — else
                            mb-6 + pt-6 would double to 48px. Either way the gap is 24px. */}
                        <div
                            className={cn(
                                activeTab === "assessment" &&
                                    effectiveAssessmentSubTab === "ai-interview" &&
                                    assessmentSubTabs.length > 1 &&
                                    "pt-6"
                            )}
                        >
                            <InterviewPlayer
                                isLoadingVideo={isLoadingVideo}
                                interviewStatus={interviewStatus}
                                s3VideoLink={videoInterview?.s3_link || ""}
                                videoInterview={videoInterview as InterviewVideo}
                                setVideoDuration={(newDuration: number) => setVideoDuration(newDuration)}
                                isHide={!(activeTab === "assessment" && effectiveAssessmentSubTab === "ai-interview")}
                            />
                        </div>
                    </div>
                </div>

                {/* Pinned comment composer — only shown on the Review tab when viewing an applicant */}
                {activeTab === "review" && applicant?.id && (
                    <div
                        className={`flex-shrink-0 bg-white dark:bg-gray-900 px-4 pt-3 pb-4 ${trialExpiredInert}`}
                        title={trialExpiredTooltip}
                        aria-disabled={isWorkspaceRestricted || maintenance.blocked}
                        {...maintenance.groupProps}
                    >
                        <div className="rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 overflow-hidden hover:border-blue-500 dark:hover:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500 dark:focus-within:border-blue-500 transition-colors">
                            <Textarea
                                ref={composerTextareaRef}
                                value={drawerComposerText}
                                onChange={(e) => setDrawerComposerText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                                        e.preventDefault();
                                        handleDrawerPost();
                                    }
                                }}
                                rows={2}
                                placeholder="Share your thoughts on this candidate…"
                                className="resize-none border-0 focus-visible:ring-0 shadow-none px-3 pt-3 pb-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 bg-transparent w-full"
                                disabled={isDrawerComposerPosting}
                            />
                            <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 dark:border-gray-700">
                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                    Visible to your team · ⌘ / Ctrl + Enter to post
                                </span>
                                <Button
                                    type="button"
                                    size="sm"
                                    onClick={handleDrawerPost}
                                    disabled={!drawerComposerText.trim() || isDrawerComposerPosting}
                                    className="h-7 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs px-3"
                                >
                                    {isDrawerComposerPosting
                                        ? <Loader2Icon className="w-3 h-3 animate-spin" />
                                        : <SendIcon className="w-3 h-3" />
                                    }
                                    Post
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Footer with action button */}
                <div
                    className={`p-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 ${trialExpiredInert}`}
                    title={trialExpiredTooltip}
                    aria-disabled={isWorkspaceRestricted || maintenance.blocked}
                    {...maintenance.groupProps}
                >
                    {/* WhatsApp reply composer — pinned in the footer, only on the
                        Messages tab, so it's visible without scrolling (#3017). */}
                    {/* A knockout-flagged candidate is paused pending HR's decision, so
                        the reply box and Follow up are hidden — nudging them forward
                        before the flag is resolved contradicts the pause. The thread
                        stays readable above, and the resolution buttons sit below. */}
                    {mayaEnabled && activeTab === "messages" && candidateWhatsappId && !whatsappThreadError && isKnockoutFlag && (
                        <p className="pb-3 text-xs text-gray-500 dark:text-gray-400">
                            Messaging is paused while this candidate is flagged. Resolve the flag below to continue.
                        </p>
                    )}
                    {mayaEnabled && activeTab === "messages" && candidateWhatsappId && !whatsappThreadError && !isKnockoutFlag && (
                        <>
                            {/* #3242: an armed HR trigger has no banner (per product) —
                                the pending state still drives the Re-send label + the
                                1×/24h rate limit on the follow-up button below. */}
                            <WhatsappReplyComposer
                                candidateId={candidateWhatsappId}
                                lastInboundAt={whatsappLastInboundAt}
                                onSent={() => setWhatsappReloadKey((k) => k + 1)}
                                trailingAction={canTriggerStep ? (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={triggerResendLimited}
                                        title={triggerResendLimited ? "The re-engage template can only be sent once per 24 hours — waiting for the candidate's reply." : undefined}
                                        className="flex-shrink-0 h-9 gap-1.5 text-xs"
                                        onClick={() => setShowTriggerStepDialog(true)}
                                    >
                                        <ZapIcon className="w-3.5 h-3.5" />
                                        {pendingHrTrigger ? "Re-send" : "Follow up"}
                                    </Button>
                                ) : undefined}
                            />
                        </>
                    )}
                    {(applicant && job) ? (
                        <div className="flex flex-row gap-3">
                            {/* Render buttons based on current funnel stage */}
                            {/* #3186: flagged candidate — mirror the row's gate actions + Reject. */}
                            {isFlaggedCandidate ? (
                                <div className="flex w-full flex-row flex-wrap gap-2">
                                    {isKnockoutFlag ? (
                                        <>
                                            <Button
                                                disabled={isFlowActionLoading}
                                                className="flex-1 min-w-[120px] h-9 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium"
                                                onClick={() => setShowKeepInJobConfirm(true)}
                                            >
                                                Keep in job
                                            </Button>
                                            {applicant?.paused_at?.suggest_job?.id && (
                                                <Button
                                                    variant="outline"
                                                    disabled={isFlowActionLoading}
                                                    className="flex-1 min-w-[120px] h-9 font-medium"
                                                    onClick={() => handleFlowResolve({ action: "reroute" }, `Candidate rerouted to "${flagSuggestJobTitle || "the suggested job"}".`)}
                                                >
                                                    Reroute to {flagSuggestJobTitle || "suggested job"}
                                                </Button>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                disabled={isFlowActionLoading}
                                                className="flex-1 min-w-[120px] h-9 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium"
                                                onClick={() => handleFlowResolve({ action: "extend", extend_due: "+48h" }, "Deadline extended — the flow resumes.")}
                                            >
                                                Extend deadline
                                            </Button>
                                            <Button
                                                variant="outline"
                                                disabled={isFlowActionLoading}
                                                className="flex-1 min-w-[120px] h-9 font-medium"
                                                onClick={() => handleFlowResolve({ action: "next_intake" }, "Deferred to the next intake (archived, restorable).")}
                                            >
                                                Next intake
                                            </Button>
                                        </>
                                    )}
                                    <Button
                                        variant="outline"
                                        disabled={isFlowActionLoading}
                                        className="flex-1 min-w-[120px] h-9 font-medium"
                                        onClick={() => setShowArchiveFlaggedConfirm(true)}
                                    >
                                        Archive
                                    </Button>
                                    <Button
                                        disabled={isFlowActionLoading}
                                        className="flex-1 min-w-[120px] h-9 gap-2 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white font-medium"
                                        onClick={handleDrawerRejectFlagged}
                                    >
                                        Reject
                                    </Button>
                                </div>
                            ) : null}
                            {activeFunnelStage === "uploads" && (
                                <>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="default"
                                                className="w-full gap-2 bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 font-medium transition-all h-9 rounded-md border border-gray-300 dark:border-gray-600 shadow-sm"
                                            >
                                                Update Candidate Status
                                                <ChevronDownIcon className="w-4 h-4 ml-2" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" className="w-72">
                                            {convertToLowerSnakeCase(applicant.status) !== "shortlisted" && (
                                                <DropdownMenuItem
                                                    onClick={() => onMarkApplicantStatus(applicant.applicant_id, "shortlisted")}
                                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Shortlisted</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">CV looks good. Ready to send a screening invitation.</div>
                                                    </div>
                                                </DropdownMenuItem>
                                            )}
                                            {convertToLowerSnakeCase(applicant.status) !== "on_hold" && (
                                                <DropdownMenuItem
                                                    onClick={() => onMarkApplicantStatus(applicant.applicant_id, "on_hold")}
                                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as On Hold</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Not the right time to progress. Candidate is kept for future consideration.</div>
                                                    </div>
                                                </DropdownMenuItem>
                                            )}
                                            {convertToLowerSnakeCase(applicant.status) !== "not_suitable" && (
                                                <DropdownMenuItem
                                                    onClick={() => onMarkApplicantStatus(applicant.applicant_id, "not_suitable")}
                                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Not Suitable</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Does not meet requirements for this role.</div>
                                                    </div>
                                                </DropdownMenuItem>
                                            )}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                    {/* #3478: ONE button; the hiring flow decides the channel.
                                        A whatsapp_first flow sends on WhatsApp (its candidates
                                        usually have a phone number and no email); web flows and
                                        legacy jobs keep the email invitation exactly as before. */}
                                    <Button
                                        onClick={() => (isWaHiringFlow
                                            ? setShowWaInvitationDialog(true)
                                            : setShowAssessmentInvitationDialog(true))}
                                        disabled={isAssessmentActionBlocked || (isWaHiringFlow ? hasNoPhone : hasNoEmail)}
                                        title={isWaHiringFlow
                                            ? (hasNoPhone ? "Candidate does not have a phone number" : undefined)
                                            : (hasNoEmail ? "Candidate does not have an email address" : undefined)}
                                        className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium transition-all h-9 rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Send Screening Invitation
                                    </Button>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="default"
                                                className="w-full gap-2 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white font-medium transition-all h-9 px-4 rounded-md shadow-sm"
                                            >
                                                Reject Candidate
                                                <ChevronDownIcon className="w-4 h-4 ml-2" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" className="w-72">
                                            <DropdownMenuItem
                                                onClick={() => setShowRejectOfferDialog(true)}
                                                disabled={hasNoEmail}
                                                className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                        Send Rejection Email
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                        {hasNoEmail ? "Candidate does not have an email address" : "Notify the candidate they were not selected for this role. Candidate moves to the Closed stage."}
                                                    </div>
                                                </div>
                                            </DropdownMenuItem>
                                            {/* Reject by WhatsApp — only for jobs on a WhatsApp-first hiring flow. */}
                                            {isWaHiringFlow && (
                                                <DropdownMenuItem
                                                    onClick={() => setShowWaRejectDialog(true)}
                                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                            Reject by WhatsApp
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                            Notify the candidate over WhatsApp using the approved rejection template. Candidate moves to the Closed stage.
                                                        </div>
                                                    </div>
                                                </DropdownMenuItem>
                                            )}
                                            <DropdownMenuItem
                                                onClick={() => onMarkApplicantStatus(applicant.applicant_id, "archived")}
                                                className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                        Mark as Archived
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                        Candidate moves to the Closed stage without email notification.
                                                    </div>
                                                </div>
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </>
                            )}
                            {activeFunnelStage === "applied" && (
                                <>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="default"
                                                className="w-full gap-2 bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 font-medium transition-all h-9 rounded-md border border-gray-300 dark:border-gray-600 shadow-sm"
                                            >
                                                Update Candidate Status
                                                <ChevronDownIcon className="w-4 h-4 ml-2" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" className="w-72">
                                            {convertToLowerSnakeCase(applicant.status) !== "shortlisted" && (
                                                <DropdownMenuItem
                                                    onClick={() => onMarkApplicantStatus(applicant.applicant_id, "shortlisted")}
                                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Shortlisted</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">CV looks good. Ready to send a screening invitation.</div>
                                                    </div>
                                                </DropdownMenuItem>
                                            )}
                                            {convertToLowerSnakeCase(applicant.status) !== "on_hold" && (
                                                <DropdownMenuItem
                                                    onClick={() => onMarkApplicantStatus(applicant.applicant_id, "on_hold")}
                                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as On Hold</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Not the right time to progress. Candidate is kept for future consideration.</div>
                                                    </div>
                                                </DropdownMenuItem>
                                            )}
                                            {convertToLowerSnakeCase(applicant.status) !== "not_suitable" && (
                                                <DropdownMenuItem
                                                    onClick={() => onMarkApplicantStatus(applicant.applicant_id, "not_suitable")}
                                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Not Suitable</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Does not meet requirements for this role.</div>
                                                    </div>
                                                </DropdownMenuItem>
                                            )}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                    {/* #3478: ONE button; the hiring flow decides the channel.
                                        A whatsapp_first flow sends on WhatsApp (its candidates
                                        usually have a phone number and no email); web flows and
                                        legacy jobs keep the email invitation exactly as before. */}
                                    <Button
                                        onClick={() => (isWaHiringFlow
                                            ? setShowWaInvitationDialog(true)
                                            : setShowAssessmentInvitationDialog(true))}
                                        disabled={isAssessmentActionBlocked || (isWaHiringFlow ? hasNoPhone : hasNoEmail)}
                                        title={isWaHiringFlow
                                            ? (hasNoPhone ? "Candidate does not have a phone number" : undefined)
                                            : (hasNoEmail ? "Candidate does not have an email address" : undefined)}
                                        className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium transition-all h-9 rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Send Screening Invitation
                                    </Button>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="default"
                                                className="w-full gap-2 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white font-medium transition-all h-9 px-4 rounded-md shadow-sm"
                                            >
                                                Reject Candidate
                                                <ChevronDownIcon className="w-4 h-4 ml-2" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" className="w-72">
                                            <DropdownMenuItem
                                                onClick={() => setShowRejectOfferDialog(true)}
                                                disabled={hasNoEmail}
                                                className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                        Send Rejection Email
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                        {hasNoEmail ? "Candidate does not have an email address" : "Notify the candidate they were not selected for this role. Candidate moves to the Closed stage."}
                                                    </div>
                                                </div>
                                            </DropdownMenuItem>
                                            {/* Reject by WhatsApp — only for jobs on a WhatsApp-first hiring flow. */}
                                            {isWaHiringFlow && (
                                                <DropdownMenuItem
                                                    onClick={() => setShowWaRejectDialog(true)}
                                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                            Reject by WhatsApp
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                            Notify the candidate over WhatsApp using the approved rejection template. Candidate moves to the Closed stage.
                                                        </div>
                                                    </div>
                                                </DropdownMenuItem>
                                            )}
                                            <DropdownMenuItem
                                                onClick={() => onMarkApplicantStatus(applicant.applicant_id, "archived")}
                                                className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                        Mark as Archived
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                        Candidate moves to the Closed stage without email notification.
                                                    </div>
                                                </div>
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </>
                            )}
                            {activeFunnelStage === "pending_assessment" && !isFlaggedCandidate && (
                                <>
                                    {flowStuckCompleted ? (
                                        <Button
                                            onClick={handleMoveToReview}
                                            disabled={isFlowActionLoading}
                                            className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium transition-all h-9 rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isFlowActionLoading ? "Moving…" : "Move to Ready for Review"}
                                        </Button>
                                    ) : applicant.status === "Link Expired" ? (
                                        <Button
                                            onClick={() => setShowAssessmentResendDialog(true)}
                                            disabled={isAssessmentActionBlocked}
                                            className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium transition-all h-9 rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Resend Screening Invitation
                                        </Button>
                                    ) : applicant.status === "Interview Incomplete" ? (
                                        <Button
                                            onClick={() => setShowAssessmentRetakeDialog(true)}
                                            disabled={isAssessmentActionBlocked}
                                            className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium transition-all h-9 rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Request Retake
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={() => setShowAssessmentInvitationReminderDialog(true)}
                                            // #WA-flow: reminders don't apply on a WhatsApp-first flow (candidates
                                            // are driven over WhatsApp, not emailed), so disable the reminder there.
                                            disabled={isAssessmentActionBlocked || isWaHiringFlow}
                                            title={isWaHiringFlow ? "This job runs on a WhatsApp-first hiring flow — candidates are driven over WhatsApp, so screening reminders don't apply." : undefined}
                                            className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium transition-all h-9 rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Send Screening Reminder
                                        </Button>
                                    )}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="default"
                                                className="w-full gap-2 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white font-medium transition-all h-9 px-4 rounded-md shadow-sm"
                                            >
                                                Reject Candidate
                                                <ChevronDownIcon className="w-4 h-4 ml-2" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" className="w-72">
                                            <DropdownMenuItem
                                                onClick={() => setShowRejectOfferDialog(true)}
                                                disabled={hasNoEmail}
                                                className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                        Send Rejection Email
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                        {hasNoEmail ? "Candidate does not have an email address" : "Notify the candidate they were not selected for this role. Candidate moves to the Closed stage."}
                                                    </div>
                                                </div>
                                            </DropdownMenuItem>
                                            {/* Reject by WhatsApp — only for jobs on a WhatsApp-first hiring flow. */}
                                            {isWaHiringFlow && (
                                                <DropdownMenuItem
                                                    onClick={() => setShowWaRejectDialog(true)}
                                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                            Reject by WhatsApp
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                            Notify the candidate over WhatsApp using the approved rejection template. Candidate moves to the Closed stage.
                                                        </div>
                                                    </div>
                                                </DropdownMenuItem>
                                            )}
                                            <DropdownMenuItem
                                                onClick={() => onMarkApplicantStatus(applicant.applicant_id, "archived")}
                                                className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                        Mark as Archived
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                        Candidate moves to the Closed stage without email notification.
                                                    </div>
                                                </div>
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </>
                            )}
                            {activeFunnelStage === "to_review" && (
                                <>
                                    {/* Update Candidate Status - outline */}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="default"
                                                className="w-full gap-2 bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 font-medium transition-all h-9 rounded-md border border-gray-300 dark:border-gray-600 shadow-sm"
                                            >
                                                Update Candidate Status
                                                <ChevronDownIcon className="w-4 h-4 ml-2" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" className="w-72">
                                            <DropdownMenuLabel className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 pt-3 pb-1">Within This Stage</DropdownMenuLabel>
                                            {convertToLowerSnakeCase(applicant.status) !== "review_scheduled" && (
                                                <DropdownMenuItem
                                                    onClick={() => onMarkApplicantStatus(applicant.applicant_id, "review_scheduled")}
                                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Review Scheduled</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">A follow-up review or interview session has been lined up.</div>
                                                    </div>
                                                </DropdownMenuItem>
                                            )}
                                            {convertToLowerSnakeCase(applicant.status) !== "shortlisted" && (
                                                <DropdownMenuItem
                                                    onClick={() => onMarkApplicantStatus(applicant.applicant_id, "shortlisted")}
                                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Shortlisted</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Candidate is a strong hire. Ready for a job offer.</div>
                                                    </div>
                                                </DropdownMenuItem>
                                            )}
                                            {convertToLowerSnakeCase(applicant.status) !== "pending_decision" && (
                                                <DropdownMenuItem
                                                    onClick={() => onMarkApplicantStatus(applicant.applicant_id, "pending_decision")}
                                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Pending Decision</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Review completed. Team is still deliberating on next steps.</div>
                                                    </div>
                                                </DropdownMenuItem>
                                            )}
                                            {convertToLowerSnakeCase(applicant.status) !== "not_suitable" && (
                                                <DropdownMenuItem
                                                    onClick={() => onMarkApplicantStatus(applicant.applicant_id, "not_suitable")}
                                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Not Suitable</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Does not meet requirements for this role.</div>
                                                    </div>
                                                </DropdownMenuItem>
                                            )}
                                            <DropdownMenuLabel className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 pt-5 pb-1">Move to Another Stage</DropdownMenuLabel>
                                            <DropdownMenuItem
                                                onClick={() => setIsInternalOfferNoteDialogOpen(true)}
                                                className="flex items-start gap-3 p-3 cursor-pointer"
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Offer Sent</div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Record that an offer was sent outside the platform. Candidate moves to the Offers stage.</div>
                                                </div>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                                onClick={() => onMarkApplicantStatus(applicant.applicant_id, "hired")}
                                                className="flex items-start gap-3 p-3 cursor-pointer"
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Hired</div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Confirm hire without sending a job offer email. Candidate moves to the Closed stage.</div>
                                                </div>
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                    {/* Email Candidate - blue */}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                disabled={hasNoEmail}
                                                title={hasNoEmail ? "Candidate does not have an email address." : undefined}
                                                className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium transition-all h-9 rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Email Candidate
                                                <ChevronDownIcon className="w-4 h-4 ml-2" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" className="w-72">
                                            <DropdownMenuItem
                                                onClick={() => setShowInterviewInvitationDialog(true)}
                                                className="flex items-start gap-3 p-3 cursor-pointer"
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Send Review Invitation</div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Invite candidates to a follow-up review or interview session.</div>
                                                </div>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                                onClick={() => setShowJobOfferDialog(true)}
                                                className="flex items-start gap-3 p-3 cursor-pointer"
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Send Job Offer Email</div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Send a job offer email to candidates. They will move to the Offers stage.</div>
                                                </div>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                                onClick={() => setShowAssessmentRetakeDialog(true)}
                                                disabled={isAssessmentActionBlocked}
                                                className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Request Interview Retake</div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Request the candidate to retake their AI interview. Candidate moves back to the Screening in Progress stage.</div>
                                                </div>
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                    {/* Reject Candidate - red */}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="default"
                                                className="w-full gap-2 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white font-medium transition-all h-9 px-4 rounded-md shadow-sm"
                                            >
                                                Reject Candidate
                                                <ChevronDownIcon className="w-4 h-4 ml-2" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" className="w-72">
                                            <DropdownMenuItem
                                                onClick={() => setShowRejectOfferDialog(true)}
                                                disabled={hasNoEmail}
                                                className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                        Send Rejection Email
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                        {hasNoEmail ? "Candidate does not have an email address" : "Notify the candidate they were not selected for this role. Candidate moves to the Closed stage."}
                                                    </div>
                                                </div>
                                            </DropdownMenuItem>
                                            {/* Reject by WhatsApp — only for jobs on a WhatsApp-first hiring flow. */}
                                            {isWaHiringFlow && (
                                                <DropdownMenuItem
                                                    onClick={() => setShowWaRejectDialog(true)}
                                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                            Reject by WhatsApp
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                            Notify the candidate over WhatsApp using the approved rejection template. Candidate moves to the Closed stage.
                                                        </div>
                                                    </div>
                                                </DropdownMenuItem>
                                            )}
                                            <DropdownMenuItem
                                                onClick={() => onMarkApplicantStatus(applicant.applicant_id, "archived")}
                                                className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                        Mark as Archived
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                        Candidate moves to the Closed stage without email notification.
                                                    </div>
                                                </div>
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </>
                            )}
                            {activeFunnelStage === "offers" && (
                                <>
                                    {/* Update Candidate Status - outline */}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="default"
                                                className="w-full gap-2 bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 font-medium transition-all h-9 rounded-md border border-gray-300 dark:border-gray-600 shadow-sm"
                                            >
                                                Update Candidate Status
                                                <ChevronDownIcon className="w-4 h-4 ml-2" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" className="w-72">
                                            <DropdownMenuLabel className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 pt-3 pb-1">Within This Stage</DropdownMenuLabel>
                                            {convertToLowerSnakeCase(applicant.status) !== "offer_resent" && (
                                                <DropdownMenuItem
                                                    onClick={() => {
                                                        setIsInternalOfferNoteDialogOpen(true)
                                                        setIsMarkOfferResent(true);
                                                    }}
                                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Offer Resent</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Record that the offer was sent again.</div>
                                                    </div>
                                                </DropdownMenuItem>
                                            )}
                                            {convertToLowerSnakeCase(applicant.status) !== "offer_declined" && (
                                                <DropdownMenuItem
                                                    onClick={() => onMarkApplicantStatus(applicant.applicant_id, "offer_declined")}
                                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Offer Declined</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Candidate has turned down the offer.</div>
                                                    </div>
                                                </DropdownMenuItem>
                                            )}
                                            <DropdownMenuLabel className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 pt-5 pb-1">Move to Another Stage</DropdownMenuLabel>
                                            <DropdownMenuItem
                                                onClick={() => onMarkApplicantStatus(applicant.applicant_id, "hired")}
                                                className="flex items-start gap-3 p-3 cursor-pointer"
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Hired</div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Confirm hire without sending a job offer email. Candidate moves to the Closed stage.</div>
                                                </div>
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                    {/* Resend Job Offer Email - blue */}
                                    <Button
                                        className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium transition-all h-9 rounded-md shadow-sm"
                                        onClick={() => setShowJobOfferDialog(true)}
                                    >
                                        Resend Job Offer Email
                                    </Button>
                                    {/* Reject Candidate - red */}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="default"
                                                className="w-full gap-2 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white font-medium transition-all h-9 px-4 rounded-md shadow-sm"
                                            >
                                                Reject Candidate
                                                <ChevronDownIcon className="w-4 h-4 ml-2" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" className="w-72">
                                            <DropdownMenuItem
                                                onClick={() => setShowRejectOfferDialog(true)}
                                                disabled={hasNoEmail}
                                                className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                        Send Rejection Email
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                        {hasNoEmail ? "Candidate does not have an email address" : "Notify the candidate they were not selected for this role. Candidate moves to the Closed stage."}
                                                    </div>
                                                </div>
                                            </DropdownMenuItem>
                                            {/* Reject by WhatsApp — only for jobs on a WhatsApp-first hiring flow. */}
                                            {isWaHiringFlow && (
                                                <DropdownMenuItem
                                                    onClick={() => setShowWaRejectDialog(true)}
                                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                            Reject by WhatsApp
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                            Notify the candidate over WhatsApp using the approved rejection template. Candidate moves to the Closed stage.
                                                        </div>
                                                    </div>
                                                </DropdownMenuItem>
                                            )}
                                            <DropdownMenuItem
                                                onClick={() => onMarkApplicantStatus(applicant.applicant_id, "archived")}
                                                className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                        Mark as Archived
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                        Candidate moves to the Closed stage without email notification.
                                                    </div>
                                                </div>
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </>
                            )}
                            {(activeFunnelStage === "closed" && applicantNewStatus === "Archived") && (
                                <Button
                                    onClick={() => onMarkApplicantStatus(applicant.applicant_id, "moved")}
                                    className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium transition-all h-9 rounded-md shadow-sm"
                                >
                                    Move Candidate
                                </Button>
                            )}
                        </div>
                    ) : (
                        <>
                            {((currentUserInfo?.job_collaborations || []).length > 0 && !job) || (currentUserInfo?.job_collaborations || []).includes(job?.id || "") ? (
                                <div className="flex gap-3 flex-col sm:flex-row">
                                    {!job && currentUserInfo?.permission !== "member" && (
                                        <Button
                                            disabled={isLoadingInvitation || isSuccessSentInvitation}
                                            className={`flex-1 px-4 py-2 w-full`}
                                            onClick={() => setInviteDialogOpen(true)}
                                        >
                                            {isLoadingInvitation ? "Sending..." : isSuccessSentInvitation ? "Invitation Sent" : "Invite to Apply"}
                                        </Button>
                                    )}
                                    {currentUserInfo?.permission === "write" && (
                                        <Button
                                            disabled={isLoadingInvitation || isSuccessSentInvitation}
                                            className={`flex-1 px-4 py-2 w-full`}
                                            onClick={() => setShortlistDialogOpen(true)}
                                        >
                                            {isShortlisted ? job ? "Shortlisted!" : "Shortlist Candidate" : job ? "Shortlist Applicant" : "Shortlist Candidate"}
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <>
                                    {(!isShowHireButton || (activeTabReview === "candidate" && shortlistedCandidates.length > 0)) && (
                                        <Button
                                            disabled={isLoadingInvitation || isSuccessSentInvitation}
                                            className={cn("flex-1 px-4 py-2 w-full", {
                                                'hidden': currentUserInfo?.permission === "member"
                                            })}
                                            onClick={() => setInviteDialogOpen(true)}
                                        >
                                            {isLoadingInvitation ? "Sending..." : isSuccessSentInvitation ? "Invitation Sent" : "Invite to Apply"}
                                        </Button>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
            </div >

            <InviteConfirmationDialogV2
                isOpen={inviteDialogOpen}
                onClose={setInviteDialogOpen}
                candidateName={displayCandidateName || ""}
                candidate_id={user?.id || ""}
                onConfirm={handleInviteConfirm}
                currentJob={job}
            />

            {
                (job && applicant) && (
                    <>
                        <JobOfferDialogV2
                            isOpen={showJobOfferDialog}
                            applicants={[
                                {
                                    ...applicant,
                                    is_selected: true
                                }
                            ]}
                            job={job}
                            onClose={setShowJobOfferDialog}
                            onSendJobOffer={handleSendJobOffer}
                        />

                        <InterviewInvitationDialogV2
                            showInterviewRoleSelector
                            isOpen={showInterviewInvitationDialog}
                            applicants={[
                                {
                                    ...applicant,
                                    is_selected: true
                                }
                            ]}
                            job={job}
                            onClose={setShowInterviewInvitationDialog}
                            onSendInvitation={handleSendInterviewInvitation}
                        />

                        <RejectCandidateDialogV2
                            isOpen={showRejectOfferDialog}
                            onClose={setShowRejectOfferDialog}
                            job={job}
                            applicants={[
                                {
                                    ...applicant,
                                    is_selected: true
                                }
                            ]}
                            onSendRejection={handleRejectCandidate}
                        />

                        {/* #3478: screening invitation on WhatsApp. The BE (#4533) writes the
                            Pending Assessment transition and the activity log itself, so there
                            is no status call here — just reload the drawer's data. */}
                        <WhatsappScreeningInvitationDialog
                            isOpen={showWaInvitationDialog}
                            jobId={job?.id || ""}
                            applicants={applicant ? [applicant] : []}
                            onClose={() => setShowWaInvitationDialog(false)}
                            onSent={(result) => {
                                if ((result?.sent?.length || 0) > 0) {
                                    snackbar.showSuccess("Screening invitation sent on WhatsApp.");
                                    onReload?.();
                                } else {
                                    const reason = result?.failed?.[0]?.reason;
                                    snackbar.showError(
                                        reason === "already_invited"
                                            ? "This candidate has already been invited."
                                            : "Screening invitation could not be sent. Please try again.",
                                    );
                                }
                            }}
                        />

                        {/* Confirm waiving a knockout flag — the candidate resumes the
                            flow past a condition HR set, so it isn't a one-click action. */}
                        <FlowGateResolveConfirmDialog
                            isOpen={showKeepInJobConfirm}
                            isLoading={isFlowActionLoading}
                            onClose={() => setShowKeepInJobConfirm(false)}
                            onConfirm={() => {
                                setShowKeepInJobConfirm(false);
                                handleFlowResolve({ action: "keep" }, "Flag waived — the flow resumes.");
                            }}
                            title="Keep this candidate in the job?"
                            confirmLabel="Keep in job"
                            description={
                                <>
                                    <p>
                                        <strong>{applicant?.applicant_name || "This candidate"}</strong> was flagged by a screening
                                        condition on this job.
                                    </p>
                                    <p>
                                        Keeping them in the job waives that flag and continues the flow from the step where it
                                        paused.
                                    </p>
                                </>
                            }
                        />

                        {/* Archiving a flagged candidate takes them out of the funnel —
                            same confirm step as its Keep in job sibling. */}
                        <FlowGateResolveConfirmDialog
                            isOpen={showArchiveFlaggedConfirm}
                            isLoading={isFlowActionLoading}
                            onClose={() => setShowArchiveFlaggedConfirm(false)}
                            onConfirm={() => {
                                setShowArchiveFlaggedConfirm(false);
                                if (applicant?.applicant_id) {
                                    onMarkApplicantStatus(applicant.applicant_id, "archived");
                                }
                            }}
                            title="Archive this candidate?"
                            confirmLabel="Archive"
                            description={
                                <>
                                    <p>
                                        <strong>{applicant?.applicant_name || "This candidate"}</strong> moves to the Closed
                                        stage and stops progressing through the flow.
                                    </p>
                                    <p>You can restore them later from the Closed tab.</p>
                                </>
                            }
                        />

                        {/* #3186/#3877: WhatsApp reject for a flagged candidate with no email. */}
                        <RejectCandidateWhatsappDialog
                            isOpen={showWaRejectDialog}
                            applicant={applicant}
                            job={job}
                            onClose={() => setShowWaRejectDialog(false)}
                            onRejected={() => { onReload?.(); onClose(); }}
                        />

                        {/* #3242: HR "Trigger Step" picker (Messages tab). */}
                        <TriggerStepDialog
                            isOpen={showTriggerStepDialog}
                            onClose={() => setShowTriggerStepDialog(false)}
                            applicationId={applicant.id}
                            steps={hiringFlowSteps}
                            stepLabel={flowStepLabel}
                            currentStepNo={flowCurrentIndex}
                            onTriggered={handleStepTriggered}
                        />

                        <AssessmentInvitationDialogV2
                            type="send-invitation"
                            isOpen={showAssessmentInvitationDialog}
                            applicants={[
                                {
                                    ...applicant,
                                    is_selected: true
                                }
                            ]}
                            job={job}
                            onClose={setShowAssessmentInvitationDialog}
                            onSendInvitation={(body) => handleSendAssessmentInvitation(body, "send-invitation")}
                        />

                        <AssessmentInvitationDialogV2
                            type="resend-invitation"
                            isOpen={showAssessmentResendDialog}
                            applicants={[
                                {
                                    ...applicant,
                                    is_selected: true
                                }
                            ]}
                            job={job}
                            onClose={setShowAssessmentResendDialog}
                            onSendInvitation={(body) => handleSendAssessmentInvitation(body, "resend-invitation")}
                        />

                        <AssessmentInvitationDialogV2
                            type="reminder"
                            isOpen={showAssessmentInvitationReminderDialog}
                            applicants={[
                                {
                                    ...applicant,
                                    is_selected: true
                                }
                            ]}
                            job={job}
                            onClose={setShowAssessmentInvitationReminderDialog}
                            onSendInvitation={(body) => handleSendAssessmentInvitation(body, "reminder")}
                        />

                        <AssessmentInvitationDialogV2
                            type="retake"
                            isOpen={showAssessmentRetakeDialog}
                            applicants={[
                                {
                                    ...applicant,
                                    is_selected: true
                                }
                            ]}
                            job={job}
                            onClose={setShowAssessmentRetakeDialog}
                            onSendInvitation={(body) => handleSendAssessmentInvitation(body, "retake")}
                        />
                    </>
                )
            }

            <ShortlistToInviteDialog
                candidate_id={user?.id || ""}
                candidate_name={displayCandidateName || ""}
                isOpen={shortlistDialogOpen}
                selectedJob={job}
                onClose={() => {
                    setShortlistDialogOpen(false);
                    // Reload shortlisted candidates list
                    const candidateId = applicant ? applicant.applicant_id : user?.id || "";
                    if (candidateId) {
                        const params: SearchShortlistedCandidateRequest = {
                            page: currentPage,
                            limit: defaultPagination.limit,
                            candidate_id: candidateId,
                            view: job ? "applicant" : "candidate",
                            job_id: job ? job.id : undefined,
                            type: reviewType,
                        };
                        searchShortlistedCandidate(params).then((resp) => {
                            if (resp.status === 200) {
                                setShortlistedCandidates(resp.data.data);
                                setMetaPagination(resp.data.meta.pagination);
                            }
                        });
                        refreshReviewCounts();
                        // Reload shortlisted count
                        if (applicant?.applicant_id && job?.id) {
                            getShortlistedByCandidateAndJobID(applicant.applicant_id, job.id).then((res) => {
                                if (res.status === 200) {
                                    const items = res.data.data || [];
                                    setShortlistedItems(items);
                                    setCountShortlisted(items.length);
                                }
                            });
                        } else {
                            getShortlistedByCandidateID(candidateId).then((res) => {
                                if (res.status === 200) {
                                    const items = res.data.data || [];
                                    setShortlistedItems(items);
                                    setCountShortlisted(items.length);
                                }
                            });
                        }
                    }
                    if (onReloadSavedBookmark) {
                        onReloadSavedBookmark();
                    }
                }}
            />

            {
                (job && applicant) && (
                    <ReviewScheduleManagerDialog
                        isOpen={isReviewScheduleManagerDialogOpen}
                        job={job}
                        applicants={[
                            applicant
                        ]}
                        onClose={() => setReviewScheduleManagerDialogOpen(false)}
                        onReload={() => {
                            if (onReload) {
                                onReload()
                            }
                        }}
                    />
                )
            }

            {
                applicant && (
                    <InternalOfferNoteDialog
                        isOpen={isInternalOfferNoteDialogOpen}
                        applicants={[applicant]}
                        onClose={() => setIsInternalOfferNoteDialogOpen(false)}
                        onSubmit={(note: string) => {
                            handleMarkSendOffer([applicant.applicant_id], note);
                            setIsInternalOfferNoteDialogOpen(false);
                        }}
                    />
                )
            }
        </>
    );
}
